<template>
    <section id="orderDetails" :class="{ 'od-embedded': embedded }">
        <div id="breadcrumb" v-if="!embedded">
            <div class="container">
                <ul>
                    <li><router-link to="/" tag="a">Home</router-link></li>
                    <li><router-link to="/account/orderhistory" tag="a">Order History</router-link></li>
                    <li class="last">Order Details</li>
                </ul>
            </div>
        </div>

        <div id="odTop">
            <div class="container">
                <!-- ===== PAGE HEADER =====
                     Order # is the headline. Date sits beneath it as
                     supporting text. Status renders as a pill badge to
                     the right — color-coded (yellow=pending, green=
                     shipped, blue=complete, red=cancelled). Class binding
                     lowercases the API status so any casing variant maps
                     to the same rule; unknown statuses fall through to a
                     neutral gray pill. -->
                <div class="order-page-header">
                    <div class="order-page-header-main">
                        <h1>Order #{{id}}</h1>
                        <p class="order-page-header-meta">Order Date: {{orderDate | formatDate}}</p>
                    </div>
                    <div class="order-page-header-status" v-if="status">
                        <span class="order-status-badge" :class="(status || '').toLowerCase()">{{ status }}</span>
                    </div>
                </div>

                <!-- ===== INFO CARDS — Sold To + Ship To =====
                     Replaces the old green/dark colored-bar headers with
                     a clean two-card layout. Same visual language as the
                     assembly cards below: white background, subtle border,
                     small red top accent, dashed header divider. flex: 1
                     for equal width; min-width keeps them readable;
                     flex-wrap stacks them on narrow screens. -->
                <div class="info-cards-row">
                    <div class="info-card">
                        <div class="info-card-header">Sold To</div>
                        <div class="info-card-body">
                            <div class="info-card-name">{{ company }}</div>
                            <p v-if="billingStreet">{{ billingStreet }}</p>
                            <p v-if="billingStreet2">{{ billingStreet2 }}</p>
                            <p>{{ billingCity }}, {{ billingState }} {{ billingPostalCode }}</p>
                            <p class="info-card-meta">Company Code: {{ companyCode }}</p>
                        </div>
                    </div>
                    <div class="info-card">
                        <div class="info-card-header">Ship To</div>
                        <div class="info-card-body">
                            <div class="info-card-name">{{ company }}</div>
                            <p v-if="shippingStreet">{{ shippingStreet }}</p>
                            <p v-if="shippingStreet2">{{ shippingStreet2 }}</p>
                            <p>{{ shippingCity }}, {{ shippingState }} {{ shippingPostalCode }}</p>
                        </div>
                    </div>
                </div>

                <!-- ===== SPECIAL INSTRUCTIONS =====
                     Only renders if instructions were attached. Quoted-
                     block treatment so user text reads as a recorded note
                     rather than continuation of page prose. -->
                <div class="special-instructions-block" v-if="specialInstructions">
                    <div class="special-instructions-label">Special Instructions</div>
                    <div class="special-instructions-text">{{ specialInstructions }}</div>
                </div>

        <!-- NOTE: #odTop > .container is intentionally left OPEN here so the
             ENTIRE left content (page header, Sold/Ship-To cards, Print band,
             the grouped PO/assembly blocks, and the Re-Order action) renders
             inside ONE standard content card — the shared brand-stripe card
             drawn by `#orderDetails #odTop > .container::before` in
             steele-consistency-fix.css. This matches the single-card layout the
             Cart page uses (`#cart #cartTop > .container`). The inner sections
             below DROP their own `.container` wrappers (which would otherwise
             each become a nested white card via the `#orderDetails .container`
             panel rule) — they're plain <div>s now. The container + #odTop are
             closed just before the modals further down. -->

        <!-- ===== PRINT ORDER ACTION =====
             Centered Print button in its own breathing-room band
             between the page header and the order detail blocks.
             Opens the Print Order(s) modal — see #print-order-modal
             below. The modal lets the user confirm the action and,
             when more than one Order (PO group) is present, pick
             which subset to print. The modal's confirm handler is
             what actually calls window.print() (the user picks
             "Save as PDF" from the destination dropdown if they
             want a PDF). The @media print stylesheet hides the
             site chrome, the Re-Order button, and this section
             itself so the printout is just the order receipt
             content. -->
        <div class="print-order-band no-print" v-if="orderDetails && orderDetails.length > 0">
            <div><!-- .container dropped: now nested in #odTop's container -->
                <button type="button"
                        class="btn btn-outline-dark btn-print-order"
                        @click="openPrintModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="btn-print-icon">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                    {{ printButtonLabel }}
                </button>
            </div>
        </div>

        <!-- ===== GROUPED ORDERS (by PO Number → Machine + Assembly) =====
             Same visual treatment as the Cart page. Each PO is one
             "Order #N" group; within each PO, each unique Machine +
             Assembly combo is its own card with collapsible parts.
             Parts duplicated across multiple backing PurchaseOrder
             records (same PO + machine + assembly) merge into a single
             row with summed quantities. View is read-only. -->
        <div id="OrderTable" class="jcs-print-doc">
            <!-- ===== PER-ORDER SECTIONS =====
                 Each Order # group is wrapped in a `print-order-section`
                 so the print stylesheet can:
                   1. Insert a `page-break-before: always` between
                      consecutive sections — every order starts on its
                      own page in the printed output, with its own
                      letterhead at the top.
                   2. Hide a section entirely when the user picked a
                      subset to print (printingSelectedOnly flag set,
                      this group's number not in selectedOrdersForPrint).
                      The `print-order-hidden` class drives that via
                      an @media print { display: none } rule.
                 Outside print (default screen view) the wrapper is
                 a transparent passthrough — no visual change. -->
            <section class="print-order-section"
                     v-for="(group, groupIndex) in groupedOrders"
                     :key="'po-' + group.number"
                     :class="{ 'print-order-hidden': isOrderHiddenForPrint(group.number) }">
                <!-- ===== PER-ORDER PRINT-ONLY LETTERHEAD =====
                     Letterhead-style banner that appears only when
                     the page is printed. Logo on the left and Parts
                     Department contact block right-aligned. Hidden
                     on screen via the scoped .print-only-header
                     { display: none } rule; revealed in print by
                     the @media print { display: flex } rule at the
                     bottom of the file.

                     Two logo strategies stacked: <img> first (same
                     SVG used by Head.vue), text "STEELE" fallback if
                     the SVG fails to load (some print engines drop
                     external SVG tags). printLogoFailed is shared
                     across every header instance — one failed load
                     flips the flag for all of them, which is the
                     correct behavior since they all reference the
                     same asset. -->
                <div class="print-only-header">
                    <div class="print-only-header-logo">
                        <img src="@/assets/img/steele-logo.svg"
                             alt="JC Steele"
                             class="print-only-header-logo-img"
                             @error="onPrintLogoError"
                             v-if="!printLogoFailed" />
                        <div class="print-only-header-logo-text"
                             v-if="printLogoFailed">
                            STEELE
                        </div>
                    </div>
                    <!-- Center: customer name + ID, between the logo and the
                         doc-id / Parts Department block. -->
                    <div class="print-only-header-customer">
                        <div class="print-only-header-customer-name">{{ company }}</div>
                        <div class="print-only-header-customer-id" v-if="companyCode">Customer ID: {{ companyCode }}</div>
                    </div>
                    <div class="print-only-header-address">
                        <!-- Doc identifier (Order #, Quote #, or
                             Acknowledgement #) — print-only,
                             renders at the top of every printed
                             page so the recipient can tie the
                             output back to a specific record. -->
                        <div class="print-only-header-doc-id">Order #: {{ id }}</div>
                        <div class="print-only-header-address-name">Parts Department</div>
                        <div>145 National Dr.</div>
                        <div>Statesville, NC 28625</div>
                        <div>P. (800) 278-3353</div>
                    </div>
                </div>

                <div><!-- .container dropped: now nested in #odTop's container -->
                    <div class="po-group">
                        <div class="order-header">
                            <span class="order-header-label">PO #{{ group.number }}</span>
                            <span class="order-header-meta">
                                {{ group.machineGroups.length }}
                                {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                            </span>
                        </div>

                        <div class="assembly-card"
                             v-for="(mg, mgIndex) in group.machineGroups"
                             :key="'asm-' + group.number + '-' + mg.key">
                            <div class="assembly-card-header">
                                <div class="assembly-card-heading">
                                    <div class="assemblyName">{{ mg.machineName }}</div>
                                    <div class="assemblyDescription">{{ mg.assemblyName }}</div>
                                </div>
                                <div class="assembly-card-po-subtotal" v-if="mgIndex === 0">
                                    <div class="assembly-card-po-label">
                                        Subtotal for PO Number:
                                        <span class="subtotal-po">{{ group.number }}</span>
                                    </div>
                                    <div class="assembly-card-po-amount" v-if="group.cost > 0">
                                        {{ group.cost | currency }}
                                    </div>
                                    <div class="assembly-card-po-amount" v-else>$0.00</div>
                                </div>
                            </div>

                        <div class="assembly-summary"
                             :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                             @click="toggleGroup(group.number, mg.key)">
                            <div class="assembly-toggle"
                                 :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                                 :aria-label="isGroupExpanded(group.number, mg.key) ? 'Collapse' : 'Expand'">
                                <span class="assembly-toggle-icon">{{ isGroupExpanded(group.number, mg.key) ? '−' : '+' }}</span>
                            </div>
                            <div class="assembly-summary-content">
                                <span class="assembly-summary-count">
                                    {{ mg.mergedParts.length }} {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                                </span>
                                <span class="code-chip"
                                      v-for="p in mg.mergedParts"
                                      :key="'chip-' + group.number + '-' + mg.key + '-' + p.partCode">
                                    {{ p.partCode }}<span class="code-qty">×{{ p.partQuantity }}</span>
                                </span>
                                <span class="assembly-summary-empty" v-if="mg.mergedParts.length === 0">
                                    No parts on this assembly.
                                </span>
                            </div>
                            <div class="assembly-summary-total"
                                 v-if="group.machineGroups.length > 1">
                                <!-- This per-assembly total is suppressed
                                     when the PO has only one assembly,
                                     because in that case it equals the
                                     PO subtotal already shown above
                                     (.assembly-card-po-subtotal) and the
                                     duplicate read as visual noise on
                                     single-assembly orders. With more
                                     than one assembly, each assembly's
                                     own subtotal IS distinct from the
                                     PO grand total, so the breakdown
                                     stays useful. -->
                                <span v-if="mg.cost > 0">{{ mg.cost | currency }}</span>
                                <span v-else>$0.00</span>
                            </div>
                        </div>

                        <div class="assembly-table-wrap" v-show="isGroupExpanded(group.number, mg.key)">
                            <b-table :fields="partsFields" :items="mg.mergedParts" responsive="sm">
                                <template v-slot:cell(item)="data">
                                    <div class="nowrap">{{ data.item.partItem }}</div>
                                </template>
                                <template v-slot:cell(code)="data">
                                    <div class="nowrap">{{ data.item.partCode }}</div>
                                </template>
                                <template v-slot:cell(description)="data">
                                    <div class="text-left">{{ data.item.partDescription }}</div>
                                </template>
                                <template v-slot:cell(qty)="data">
                                    {{ data.item.partQuantityPerMachine }}
                                </template>
                                <template v-slot:cell(price)="data">
                                    {{ data.item.partPrice | currency }}
                                </template>
                                <template v-slot:cell(orderqty)="data">
                                    {{ data.item.partQuantity }}
                                </template>
                                <template v-slot:cell(cost)="data">
                                    {{ data.item.cost | currency }}
                                </template>
                            </b-table>
                            <!-- Print-only totals line, below (not inside) the table. -->
                            <div class="parts-total-line">
                                <span class="parts-total-strong">Total:</span>
                                <span class="parts-total-amount">{{ mg.mergedParts.reduce((s, p) => s + (Number(p.partQuantity) || 0), 0) }}</span> Parts at
                                <span class="parts-total-amount">{{ mg.mergedParts.reduce((s, p) => s + (Number(p.cost) || 0), 0) | currency }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Per-order total: each PO prints on its own page and ends
                     with its own bottom-line total. -->
                <div class="cart-grand-total">
                    <div class="cart-grand-total-label">Order Total:</div>
                    <div class="cart-grand-total-value"><span class="grand-total-parts-prefix">{{ groupPartsCount(group) }} Parts at </span>{{ group.cost | currency }}</div>
                </div>
            </div>
            </section>

            <!-- The Order Total now prints per PO, inside each section above,
                 so each order's page ends with its own bottom-line total. -->

            <!-- ===== RE-ORDER ACTION =====
                 Sits where Cart.vue puts its Place Order button so a
                 customer reviewing a past order has the same vertical
                 muscle-memory as when they originally checked out:
                 information first, action button at the bottom.
                 no-print so the printed receipt doesn't carry an
                 interactive button. -->
            <div id="finalActions" class="no-print" v-if="orderDetails && orderDetails.length > 0 && !embedded">
                <div><!-- .container dropped: now nested in #odTop's container -->
                    <div class="row justify-content-center">
                        <div class="col-lg-6">
                            <a @click="onReorderClick()"
                               class="btn btn-primary btn-place-order"
                               :title="'Re-order for ' + orderCompanyDisplay"
                               :aria-label="'Re-order for ' + orderCompanyDisplay">Re-Order</a>
                            <p class="reorder-disclaimer">
                                Pricing and availability are subject to change.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div><!-- /#OrderTable -->
            </div><!-- /.container (#odTop) — wraps the whole left content now -->
        </div><!-- /#odTop -->

        <!-- ===== PRINT ORDER(S) MODAL =====
             Opens when the user clicks the Print Order(s) button
             in the .print-order-band above. Always shown on click —
             even for the single-order case — so the user has a
             consistent confirmation step that mirrors the
             Print Quote(s) modals on SavedRFQ / RFQDetails.

             Two flavors driven off groupedOrders.length:
               1 order   — banner reads "Ready to Print Your Order?",
                           no checkboxes (one row, always selected),
                           primary button reads "Print Order".
               N orders  — banner reads "Ready to Print Orders?",
                           a checkbox per Order # so the user can
                           deselect ones they don't want to print,
                           primary button reads "Print All Orders"
                           when everything's selected, "Print N
                           Selected Orders" when a subset is.

             no-print on the modal-class so an accidentally-open
             dialog doesn't bleed into the print output. -->
        <b-modal id="print-order-modal"
                 v-model="showPrintModal"
                 :title="printModalTitle"
                 size="md"
                 centered
                 modal-class="print-order-modal no-print"
                 :ok-title="printModalConfirmLabel"
                 ok-variant="primary"
                 cancel-title="Cancel"
                 cancel-variant="outline-secondary"
                 :ok-disabled="!hasSelectedOrdersForPrint"
                 @ok.prevent="confirmPrint">

            <!-- Brand-red banner — same visual treatment as the
                 Modify Quote / Finalize / Print modals across the
                 quote pages so the order/quote flows feel
                 consistent. Title varies by single vs multi-order
                 case (see printModalBannerTitle computed). -->
            <div class="modify-restored-banner print-order-banner">
                <div class="modify-restored-title">
                    {{ printModalBannerTitle }}
                </div>
                <div class="modify-restored-body">
                    <span v-if="groupedOrders.length === 1">
                        Confirm below — then click <strong>Print Order</strong>
                        to open the printable order receipt.
                    </span>
                    <span v-else>
                        Confirm the POs below — then click
                        <strong>{{ printModalConfirmLabel }}</strong> to open
                        the printable order receipt. Each selected PO
                        prints on its own page with the JC Steele
                        letterhead at the top.
                    </span>
                </div>
            </div>

            <p class="convert-modal-intro" v-if="groupedOrders.length > 1">
                Select which POs you want to include on the printed
                output.
            </p>

            <!-- Order rows. For a single-PO order we still
                 render the row (just without a selection
                 checkbox) so the user sees what they're about
                 to print. For multiple POs, each row gets a
                 b-form-checkbox bound by the PO number — same
                 container/styling vocabulary
                 (.convert-quote-list, .convert-quote-row,
                 .convert-quote-row-grouped, etc.) used by the
                 Finalize / Save for Later picker on
                 /cart/rfqsaveditems so the pages feel like
                 siblings.

                 Per-row content mirrors the on-page layout:
                 PO #{number}, assembly count, total cost,
                 and a small list of each constituent
                 assembly. -->
            <div class="convert-quote-list">
                <div v-for="(group, groupIndex) in groupedOrders"
                     :key="'print-pick-' + group.number"
                     class="convert-quote-row convert-quote-row-grouped">
                    <!-- Multi-order: bound checkbox per row. -->
                    <b-form-checkbox v-if="groupedOrders.length > 1"
                                     :checked="isOrderSelectedForPrint(group.number)"
                                     @change="setOrderSelectionForPrint(group.number, $event)">
                        <div class="convert-quote-row-main">
                            <strong class="convert-quote-row-label">
                                PO #{{ group.number }}
                            </strong>
                        </div>
                        <div class="convert-quote-row-sub">
                            {{ group.machineGroups.length }}
                            {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                            <span class="convert-quote-row-amount"
                                  v-if="group.cost > 0">
                                · {{ group.cost | currency }}
                            </span>
                        </div>
                        <div class="convert-quote-row-assemblies"
                             v-if="group.machineGroups.length > 0">
                            <div v-for="mg in group.machineGroups"
                                 :key="'print-pick-asm-' + group.number + '-' + mg.key"
                                 class="convert-quote-row-assembly">
                                <span v-if="mg.machineName">{{ mg.machineName }}</span>
                                <template v-if="mg.assemblyName"> · {{ mg.assemblyName }}</template>
                                <template v-if="mg.mergedParts && mg.mergedParts.length">
                                    · {{ mg.mergedParts.length }}
                                    {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                                </template>
                            </div>
                        </div>
                    </b-form-checkbox>

                    <!-- Single-order: no checkbox, just the same row
                         layout as the multi-order case minus the form
                         control. .print-order-row-static is the
                         wrapper that replaces b-form-checkbox in
                         that branch. -->
                    <div v-else class="print-order-row-static">
                        <div class="convert-quote-row-main">
                            <strong class="convert-quote-row-label">
                                PO #{{ group.number }}
                            </strong>
                        </div>
                        <div class="convert-quote-row-sub">
                            {{ group.machineGroups.length }}
                            {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                            <span class="convert-quote-row-amount"
                                  v-if="group.cost > 0">
                                · {{ group.cost | currency }}
                            </span>
                        </div>
                        <div class="convert-quote-row-assemblies"
                             v-if="group.machineGroups.length > 0">
                            <div v-for="mg in group.machineGroups"
                                 :key="'print-static-asm-' + group.number + '-' + mg.key"
                                 class="convert-quote-row-assembly">
                                <span v-if="mg.machineName">{{ mg.machineName }}</span>
                                <template v-if="mg.assemblyName"> · {{ mg.assemblyName }}</template>
                                <template v-if="mg.mergedParts && mg.mergedParts.length">
                                    · {{ mg.mergedParts.length }}
                                    {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Select All / Deselect All toggle — only when
                 there's more than one order (single-order case has
                 no checkbox to flip anyway). Mirrors the Finalize
                 modal on /cart/rfqsaveditems for visual
                 consistency. -->
            <div class="convert-quote-toggle-all"
                 v-if="groupedOrders.length > 1">
                <a href="#"
                   @click.prevent="toggleSelectAllOrdersForPrint">
                    {{ selectedOrdersForPrint.length === groupedOrders.length
                        ? 'Deselect All'
                        : 'Select All' }}
                </a>
            </div>
        </b-modal>

        <!-- ===== RE-ORDER COMPANY MISMATCH PROMPT (Feature 1) =====
             Mirror of the dashboard prompt. Opens when the user
             clicks Re-Order on an order placed by a DIFFERENT company
             than the one currently selected. Offers the always-valid
             "switch to the order's company" path and the cross-company
             "add to my company" path. When the companies match, the
             modal never opens (onReorderClick goes straight to the
             existing reorder()). -->
        <b-modal
            id="reorderMismatchModal"
            v-model="showReorderMismatchModal"
            title="Re-order for a different company"
            hide-footer
            centered
        >
            <!-- CHOICE STATE — unchanged two-option prompt. Hidden once an
                 "Add to my company" attempt fails (reorderFailState set). -->
            <div class="reorder-mismatch-body" v-if="!reorderFailState">
                <p class="reorder-mismatch-lead">
                    This order was placed by a different company.
                </p>
                <ul class="reorder-mismatch-facts">
                    <li>
                        Placed by:
                        <strong>{{ orderCompanyDisplay }}</strong>
                    </li>
                    <li>
                        You're in:
                        <span>{{ currentCompanyDisplayLabel }}</span>
                    </li>
                </ul>
                <p class="reorder-mismatch-help">
                    Which company should this re-order be added to?
                </p>
                <div class="reorder-mismatch-actions">
                    <button
                        type="button"
                        class="btn btn-primary reorder-mismatch-btn"
                        :disabled="reorderMismatchBusy"
                        @click="reorderForOrderCompany"
                    >
                        Add to {{ orderCompanyName }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-dark reorder-mismatch-btn"
                        :disabled="reorderMismatchBusy"
                        @click="reorderForMyCompany"
                    >
                        Add to my company ({{ currentCompanyNameLabel }})
                    </button>
                </div>
                <p class="reorder-mismatch-caveat">
                    Adding to your company copies the parts from this order. If
                    any of those parts belong to the other company's machines,
                    they may not be valid for your company.
                </p>
            </div>

            <!-- FAILURE STATE — shown when "Add to my company" was rejected.
                 Surfaces the REAL error, the specific machine(s) / assembly /
                 wear part(s) missing from the current company's setup, a
                 plain-English suggested fix, and (Master only) an action to
                 add the missing items + re-try. Mirror of the dashboard. -->
            <div class="reorder-mismatch-body reorder-fail" v-else>
                <p class="reorder-fail-title">
                    <span aria-hidden="true">⚠</span>
                    Couldn't add this order to {{ currentCompanyNameLabel }}
                </p>

                <p class="reorder-fail-reason">{{ reorderFailReason }}</p>

                <div v-if="reorderMissing.length" class="reorder-fail-missing">
                    <p class="reorder-fail-subhead">
                        These items from the order aren't set up for
                        {{ currentCompanyNameLabel }}:
                    </p>
                    <ul class="reorder-fail-list">
                        <li v-for="m in reorderMissing" :key="m.key">
                            <span class="reorder-fail-kind">{{ m.kindLabel }}</span>
                            <span class="reorder-fail-name">{{ m.label }}</span>
                            <span class="reorder-fail-fix">{{ m.fix }}</span>
                        </li>
                    </ul>
                </div>
                <div v-else-if="reorderFailDiagnosing" class="reorder-fail-diagnosing">
                    Checking which items are missing…
                </div>

                <div v-if="canAddMissing" class="reorder-fail-actions">
                    <button
                        type="button"
                        class="btn btn-primary reorder-mismatch-btn"
                        :disabled="reorderMismatchBusy"
                        @click="addMissingToMyCompany(false)"
                    >
                        {{ reorderMismatchBusy
                            ? 'Adding…'
                            : ('Add ' + addableMissingSummary + ' for future orders & quotes') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-dark reorder-mismatch-btn"
                        :disabled="reorderMismatchBusy"
                        @click="addMissingToMyCompany(true)"
                    >
                        {{ reorderMismatchBusy ? 'Adding…' : 'Add just this time' }}
                    </button>
                    <p class="reorder-fail-temp-note">
                        “Just this time” adds {{ addableMissingSummary }} to
                        {{ currentCompanyNameLabel }} so you can place this re-order now.
                        Removable later in Account Setup; auto-removed after this order is
                        placed / quote is finalized.
                    </p>
                </div>

                <div v-if="reorderCanRetry" class="reorder-fail-retry">
                    <p class="reorder-fail-retry-note">
                        <span aria-hidden="true">✓</span> Added to
                        {{ currentCompanyNameLabel }}. You can re-try the re-order now.
                    </p>
                    <button
                        type="button"
                        class="btn btn-primary reorder-mismatch-btn"
                        :disabled="reorderMismatchBusy"
                        @click="retryReorderForMyCompany"
                    >
                        {{ reorderMismatchBusy ? 'Re-trying…' : 'Re-try “Add to my company”' }}
                    </button>
                </div>

                <div class="reorder-fail-alt">
                    <button
                        type="button"
                        class="btn btn-outline-dark reorder-mismatch-btn"
                        :disabled="reorderMismatchBusy"
                        @click="reorderForOrderCompany"
                    >
                        Add to {{ orderCompanyName }} instead
                    </button>
                </div>
            </div>
        </b-modal>
    </section>
</template>

<script>
import { mapGetters } from "vuex";
import FeApiService from "@/assets/js/FeApiService";
import { addTempSetupAdds } from "@/assets/js/tempSetupAdds";
const apiService = new FeApiService();

import 'expose-loader?$!expose-loader?jQuery!jquery';
import 'semantic-ui-dropdown/dropdown.js';

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
    name: 'OrderDetails',
    props: {
        // When embedded in another view (e.g. the Sales Insights "Orders In Process" modal), the parent passes the
        // already-fetched order payload (same array shape as GET /order/{id}) so we skip the route-param fetch;
        // `embedded` hides the page chrome (breadcrumb / print / re-order), leaving just the order body.
        orderData: { type: Array, default: null },
        embedded: { type: Boolean, default: false },
    },
    computed: {
        ...mapGetters({ currentUser: "currentUser" }),
        // Defensive master/admin detection — the reorder-failure setup
        // diagnosis + add-missing actions hit Master-gated endpoints
        // (setup-summary / setup-link-add), so only masters can use them.
        // Same shape-tolerant check as the dashboard (Account/Index.vue).
        isMasterUser() {
            const u = this.currentUser || {};
            if (u.isAdmin === true || u.isMaster === true) return true;
            const matchesRole = function (val) {
                if (!val) return false;
                const s =
                    typeof val === "string"
                        ? val
                        : (val && val.name) || (val && val.role) || "";
                const low = String(s).toLowerCase();
                return low === "master" || low === "admin";
            };
            if (matchesRole(u.role)) return true;
            if (Array.isArray(u.roles) && u.roles.some(matchesRole)) return true;
            return false;
        },
        // Two-level grouping mirroring the Cart page:
        //   1. By PO number (top level — one "Order #N" block each)
        //   2. Within each PO, by Machine + Assembly combination
        // Within each subgroup, parts are deduped by partCode (qty +
        // cost summed) so duplicate backing PurchaseOrder records
        // collapse into a clean per-part view.
        groupedOrders() {
            return this.buildGroupedOrders(this.orderDetails);
        },

        // Total quantity of all parts across every PO/assembly — the
        // "N Parts" shown beside the grand total on the printout.
        totalPartsCount() {
            var n = 0;
            (this.groupedOrders || []).forEach(function (g) {
                (g.machineGroups || []).forEach(function (mg) {
                    (mg.mergedParts || []).forEach(function (p) {
                        n += Number(p.partQuantity) || 0;
                    });
                });
            });
            return n;
        },

        // ===== PRINT BUTTON / MODAL TEXT (singular vs plural) =====
        // Drives the visible label on the .btn-print-order button
        // and the modal title. Singular / plural based purely on
        // groupedOrders.length so a one-PO order reads naturally
        // instead of "Print Orders" with one item.
        printButtonLabel() {
            return this.groupedOrders.length > 1 ? 'Print Orders' : 'Print Order';
        },
        printModalTitle() {
            return this.groupedOrders.length > 1 ? 'Print Orders' : 'Print Order';
        },

        // ===== BANNER TITLE =====
        // The big red callout at the top of the modal. Singular
        // form for the one-order case — "Ready to Print Your
        // Order?". Plural form covers the multi-PO case (which
        // shows up here when an order was placed against multiple
        // POs in one cart submission).
        printModalBannerTitle() {
            return this.groupedOrders.length > 1
                ? 'Ready to Print Orders?'
                : 'Ready to Print Your Order?';
        },

        // ===== CONFIRM BUTTON LABEL =====
        // Three flavors:
        //   - Single PO                              → "Print Order"
        //   - Multi PO, all selected                 → "Print All POs"
        //   - Multi PO, partial selection            → "Print N Selected POs"
        // Each printed page is one PO; the confirm button
        // states exactly what will land on paper. The single-
        // PO case keeps the friendlier "Print Order" framing
        // (the order is its one PO) — only the multi-PO
        // selection cases need the precise PO terminology to
        // tell the user how many pages they'll get.
        printModalConfirmLabel() {
            var total = this.groupedOrders.length;
            if (total <= 1) return 'Print Order';
            var picked = this.selectedOrdersForPrint.length;
            if (picked === 0) return 'Print';
            if (picked === total) return 'Print All POs';
            return 'Print ' + picked + ' Selected ' +
                (picked === 1 ? 'PO' : 'POs');
        },

        // ===== CONFIRM BUTTON ENABLE STATE =====
        // Disable Print when nothing is checked. Single-order
        // case is always enabled because there's nothing to
        // uncheck (the row renders without a checkbox at all).
        hasSelectedOrdersForPrint() {
            if (this.groupedOrders.length <= 1) return true;
            return this.selectedOrdersForPrint.length > 0;
        },

        // ===== RE-ORDER MISMATCH DISPLAY HELPERS (Feature 1) =====
        // The order's company NAME only (button label).
        orderCompanyName() {
            return this.company || "the order's company";
        },
        // Full "Name – CODE" for the order's company. Uses the data
        // fields populated in viewOrderSuccessful (company name +
        // code). Falls back to the numeric id, then a generic label.
        orderCompanyDisplay() {
            var name = this.company || "";
            var idPart =
                this.companyCode ||
                (this.orderCompanyId != null ? this.orderCompanyId : "");
            if (name && idPart !== "") return name + " – " + idPart;
            if (name) return name;
            if (idPart !== "") return "Company " + idPart;
            return "this company";
        },
        // Full "Name - (CODE)" label for the CURRENT company from the
        // select-list (the exact text the header shows). Falls back to
        // "Company <id>".
        currentCompanyDisplayLabel() {
            var cid = localStorage.companyId || "";
            var match = (this.companies || []).find(function (c) {
                return String(c.value) === String(cid);
            });
            if (match && match.text) return match.text;
            return cid ? "Company " + cid : "your company";
        },
        // The CURRENT company's NAME only — strips the trailing
        // " - (CODE)" from the select-list text. Used for the
        // "Add to my company (Name)" button label.
        currentCompanyNameLabel() {
            var full = this.currentCompanyDisplayLabel;
            var idx = full.indexOf(" - (");
            return idx > 0 ? full.substring(0, idx) : full;
        },

        // ===== FAILURE-PANEL HELPERS (mirror of dashboard) =====
        // Add-able missing items — machines + wear parts (setup-link-add
        // supports both). Assemblies are listed for the fix but not added.
        addableMissing() {
            return (this.reorderMissing || []).filter(function (m) {
                return m.kind === "machine" || m.kind === "wear";
            });
        },
        // Master-only (setup APIs are Master-gated) AND add-able items exist
        // AND not yet added.
        canAddMissing() {
            return (
                this.isMasterUser &&
                !this.reorderCanRetry &&
                this.addableMissing.length > 0
            );
        },
        // "2 machines + 1 wear part" summary for the add button label.
        addableMissingSummary() {
            var machines = this.addableMissing.filter(function (m) {
                return m.kind === "machine";
            }).length;
            var wears = this.addableMissing.filter(function (m) {
                return m.kind === "wear";
            }).length;
            var parts = [];
            if (machines)
                parts.push(machines + (machines > 1 ? " machines" : " machine"));
            if (wears)
                parts.push(wears + (wears > 1 ? " wear parts" : " wear part"));
            return parts.join(" + ") || "the missing items";
        },
    },
    mounted() {
        // Embedded (modal) use: render the payload the parent already fetched (report-scoped) and skip the
        // route-param fetch + company list (re-order is hidden when embedded, so getCompanies isn't needed).
        if (this.orderData) { this.viewOrderSuccessful(this.orderData); return; }
        this.viewOrder();
        // Load the company select-list (same source as the header) so
        // the reorder mismatch prompt can show the CURRENT company's
        // display name. Non-blocking.
        this.getCompanies();
    },
    data() {
        return {
            id: "",
            status: "",
            specialInstructions: "",
            orderDate: "",
            orderDetails: [],
            total: "",
            company: "",
            companyCode: "",
            billingCompanyName: "",
            billingStreet: "",
            billingStreet2: "",
            billingCity: "",
            billingState: "",
            billingPostalCode: "",
            shippingCompanyName: "",
            shippingStreet: "",
            shippingStreet2: "",
            shippingCity: "",
            shippingState: "",
            shippingPostalCode: "",
            // Tracks which assembly cards are expanded — keyed by
            // PO + machine/assembly key. All start collapsed for a
            // clean overview-first view.
            expandedGroups: {},
            partsFields: [
                'item',
                'code',
                'description',
                { key: 'qty', label: 'QTY PER MACHINE' },
                'price',
                { key: 'orderqty', label: 'ORDERED' },
                'cost'
            ],
            clear: '',

            // ===== PRINT MODAL STATE =====
            // showPrintModal — drives the b-modal v-model. Flipped
            // true by openPrintModal (button click), back to false
            // by Cancel / dismiss / successful print.
            //
            // selectedOrdersForPrint — array of group.number (PO
            // number) values representing which orders will appear
            // in the print output. Populated to "all of them" when
            // the modal opens; the user can deselect rows in the
            // multi-order case. confirmPrint short-circuits if
            // this is empty (the modal's primary button is
            // disabled in that state, but we double-check).
            //
            // printingSelectedOnly — set true for the duration of
            // window.print(). The per-order section wrapper hides
            // itself in print when this flag is true AND the
            // section's group.number isn't in
            // selectedOrdersForPrint, via the print-order-hidden
            // class plumbed through isOrderHiddenForPrint(). Reset
            // to false in confirmPrint's finally so a stale flag
            // can't suppress a future full-page print.
            //
            // printLogoFailed — flipped true by onPrintLogoError
            // when the SVG <img> in the per-order header fails to
            // load. Each header instance v-if's against this flag
            // and renders the text "STEELE" fallback when true.
            showPrintModal: false,
            selectedOrdersForPrint: [],
            printingSelectedOnly: false,
            printLogoFailed: false,

            // ===== RE-ORDER MISMATCH PROMPT (Feature 1) =====
            // orderCompanyId — the company this order belongs to,
            //   captured in viewOrderSuccessful from
            //   request[0].company.id. Compared against
            //   localStorage.companyId in onReorderClick.
            // companies — the [{value,text}] select-list (same source
            //   as the header) used to resolve the CURRENT company's
            //   display label.
            // showReorderMismatchModal / reorderMismatchBusy — drive
            //   the b-modal and disable its buttons mid-request.
            orderCompanyId: null,
            companies: [],
            showReorderMismatchModal: false,
            reorderMismatchBusy: false,

            // ===== "ADD TO MY COMPANY" FAILURE STATE =====
            // Mirror of the dashboard (Account/Index.vue). reorderFailState
            // swaps the choice prompt for the failure panel; reorderFailReason
            // is the REAL backend error; reorderMissing is the diffed list of
            // missing machines/assemblies/wear parts + suggested fixes;
            // reorderFailDiagnosing flags the in-flight setup fetch+diff;
            // reorderCanRetry shows the re-try affordance after a successful
            // add. See the matching block in Account/Index.vue for the full
            // rationale.
            reorderFailState: false,
            reorderFailReason: "",
            reorderMissing: [],
            reorderFailDiagnosing: false,
            reorderCanRetry: false,
            // Guards the best-effort failure event-log POST so a single
            // cross-company-reorder failure logs at most once even though the
            // catch handler and the async diagnosis both reach the logger.
            reorderFailLogged: false,
            // Once-guard for the SUCCESS event-log POST so a single reorder
            // logs at most one success event across all reorder entry points.
            reorderSuccessLogged: false,
        }
    },
    methods: {
        // Total quantity of all parts within ONE PO group — drives the
        // "N Parts" on that PO's bottom-line total.
        groupPartsCount(group) {
            var n = 0;
            (group.machineGroups || []).forEach(function (mg) {
                (mg.mergedParts || []).forEach(function (p) {
                    n += Number(p.partQuantity) || 0;
                });
            });
            return n;
        },
        // ===== PRINT MODAL: OPEN =====
        // Click handler for the .btn-print-order button. Opens
        // the confirmation modal (always — even for a single
        // order, so the experience is consistent across counts).
        // Pre-selects every order so "Print All" is the
        // zero-click default; the user only has to interact
        // when they want a subset.
        //
        // Edge case: groupedOrders is empty (no orders loaded
        // yet, or viewOrder() failed). Surface a toast and skip
        // opening the modal — matches the same not-ready-yet
        // pattern used elsewhere.
        openPrintModal() {
            if (!this.groupedOrders || this.groupedOrders.length === 0) {
                this.$bvToast.toast(
                    "Order data isn't ready yet — please try again in a moment.",
                    {
                        title: 'Cannot print',
                        autoHideDelay: 4000,
                        variant: 'warning',
                        solid: true,
                    }
                );
                return;
            }
            // Reset selection to "everything checked" each time
            // the modal opens so a previous partial selection
            // doesn't linger. PO numbers are the source-of-truth
            // identifier for matching against the per-order
            // print sections.
            this.selectedOrdersForPrint = this.groupedOrders.map(function (g) {
                return g.number;
            });
            // Embedded (e.g. the Sales Insights order-detail modal): the PO-picker b-modal would open BEHIND the
            // host modal (its default z-index is far below), so the user would see nothing happen. Skip the picker
            // and print the whole order directly (selection is already "all" above).
            if (this.embedded) { this.confirmPrint(); return; }
            this.showPrintModal = true;
        },

        // ===== PRINT MODAL: CONFIRM =====
        // Triggered by the modal's primary button. Sets the
        // printingSelectedOnly flag (which the per-order section
        // wrapper consumes via isOrderHiddenForPrint), waits
        // for Vue to flush the class change to the DOM, then
        // fires window.print(). The browser's print dialog is
        // modal — by the time it returns we're safe to roll
        // the flag back so a subsequent unrelated print (e.g.
        // browser hotkey) shows the full page.
        //
        // Wrapped in try/finally so an exception during print
        // (rare, but possible — some embedded contexts
        // disallow it) doesn't strand the page in
        // printingSelectedOnly=true state, which would silently
        // hide order sections on future prints.
        confirmPrint() {
            var self = this;
            // Defensive — ok-disabled covers the multi-order
            // empty case, but this also gates accidental
            // programmatic calls.
            if (!this.hasSelectedOrdersForPrint) return;

            this.printingSelectedOnly = true;
            this.showPrintModal = false;

            // Wait for the section wrappers to pick up the
            // print-order-hidden class via reactivity. Without
            // nextTick, window.print() runs against the
            // pre-update DOM and unselected orders still
            // appear.
            this.$nextTick(function () {
                try {
                    window.print();
                } finally {
                    // Reset on the NEXT tick so any frame the
                    // print engine captures during a slow
                    // dialog open still has the hidden state
                    // applied. window.print() is synchronous
                    // in mainstream browsers, but the
                    // belt-and-braces approach here costs
                    // nothing.
                    self.$nextTick(function () {
                        self.printingSelectedOnly = false;
                    });
                }
            });
        },

        // ===== PRINT SELECTION HELPERS =====
        // isOrderSelectedForPrint — checkbox v-bind helper.
        // isOrderHiddenForPrint   — wrapper class helper.
        //                           Returns true when we're in
        //                           selected-only print mode
        //                           AND this order isn't on the
        //                           picked list. The wrapper
        //                           toggles print-order-hidden
        //                           based on the result; the
        //                           @media print rule does the
        //                           actual hiding.
        // setOrderSelectionForPrint — checkbox @change handler.
        // toggleSelectAllOrdersForPrint — Select / Deselect
        //                                 All link at the
        //                                 bottom of the modal.
        isOrderSelectedForPrint(poNumber) {
            return this.selectedOrdersForPrint.indexOf(poNumber) !== -1;
        },
        isOrderHiddenForPrint(poNumber) {
            if (!this.printingSelectedOnly) return false;
            return !this.isOrderSelectedForPrint(poNumber);
        },
        setOrderSelectionForPrint(poNumber, checked) {
            var idx = this.selectedOrdersForPrint.indexOf(poNumber);
            if (checked && idx === -1) {
                // Push a copy so v-model bindings further up
                // see a new array reference and re-render
                // reliably (Vue 2 can miss in-place mutations
                // of arrays held on data props in some edge
                // cases).
                this.selectedOrdersForPrint = this.selectedOrdersForPrint.concat([poNumber]);
            } else if (!checked && idx !== -1) {
                var next = this.selectedOrdersForPrint.slice();
                next.splice(idx, 1);
                this.selectedOrdersForPrint = next;
            }
        },
        toggleSelectAllOrdersForPrint() {
            // If everything is currently checked, clear;
            // otherwise select all. Mirrors the Print Quote
            // modal's same toggle.
            if (this.selectedOrdersForPrint.length === this.groupedOrders.length) {
                this.selectedOrdersForPrint = [];
            } else {
                this.selectedOrdersForPrint = this.groupedOrders.map(function (g) {
                    return g.number;
                });
            }
        },

        // Logo <img> in the print header failed to load —
        // flip the flag so the v-if swaps to the text fallback
        // ("STEELE") visible in the next paint, before the
        // user triggers print. Shared across every per-order
        // header instance — one error event flips the flag for
        // all of them, which is what we want since they all
        // reference the same asset.
        onPrintLogoError() {
            this.printLogoFailed = true;
        },

        // Build the two-level grouped structure consumed by the template.
        // Adapted from Cart.vue's buildGroupedOrders for the flatter part
        // shape returned by the order-details API (`partCode`, `partQuantity`,
        // `cost` directly on each part rather than nested under `lineItem.part`).
        buildGroupedOrders(orders) {
            var poGroups = {};

            (orders || []).forEach(function (order) {
                var poNumber = order.purchaseOrderNumber || '(no PO)';

                if (!poGroups[poNumber]) {
                    poGroups[poNumber] = {
                        number: poNumber,
                        cost: 0,
                        _machineMap: {}
                    };
                }
                var poGroup = poGroups[poNumber];

                var machineName = order.machineDescription || '';
                var assemblyName = order.assemblyName || '';
                var mKey = machineName + '||' + assemblyName;

                if (!poGroup._machineMap[mKey]) {
                    poGroup._machineMap[mKey] = {
                        key: mKey,
                        machineName: machineName,
                        assemblyName: assemblyName,
                        cost: 0,
                        _partsMap: {}
                    };
                }
                var mGroup = poGroup._machineMap[mKey];
                mGroup.cost += Number(order.cost) || 0;
                poGroup.cost += Number(order.cost) || 0;

                (order.parts || []).forEach(function (part) {
                    if (!part) return;
                    var code = part.partCode || ('__id_' + (part.partItem || Math.random()));
                    if (!mGroup._partsMap[code]) {
                        mGroup._partsMap[code] = {
                            partItem: part.partItem,
                            partCode: part.partCode,
                            partDescription: part.partDescription,
                            partPrice: part.partPrice,
                            partQuantityPerMachine: part.partQuantityPerMachine,
                            partQuantity: Number(part.partQuantity) || 0,
                            cost: Number(part.cost) || 0
                        };
                    } else {
                        mGroup._partsMap[code].partQuantity += Number(part.partQuantity) || 0;
                        mGroup._partsMap[code].cost += Number(part.cost) || 0;
                    }
                });
            });

            return Object.keys(poGroups).map(function (poKey) {
                var po = poGroups[poKey];
                return {
                    number: po.number,
                    cost: po.cost,
                    machineGroups: Object.keys(po._machineMap).map(function (mKey) {
                        var mg = po._machineMap[mKey];
                        return {
                            key: mg.key,
                            machineName: mg.machineName,
                            assemblyName: mg.assemblyName,
                            cost: mg.cost,
                            mergedParts: Object.keys(mg._partsMap).map(function (k) {
                                return mg._partsMap[k];
                            })
                        };
                    })
                };
            });
        },

        // ===== ASSEMBLY EXPAND/COLLAPSE =====
        groupExpandKey(poNumber, mgKey) {
            return poNumber + '__' + mgKey;
        },
        isGroupExpanded(poNumber, mgKey) {
            return !!this.expandedGroups[this.groupExpandKey(poNumber, mgKey)];
        },
        toggleGroup(poNumber, mgKey) {
            var k = this.groupExpandKey(poNumber, mgKey);
            this.$set(this.expandedGroups, k, !this.expandedGroups[k]);
        },

        viewOrder() {
            apiService
                .viewOrder({
                    id: this.$route.params.id
                })
                .then(request => {
                        this.viewOrderSuccessful(request);
                    })
                .catch(err => this.viewOrderFailed(err));
        },
        viewOrderSuccessful(request) {
            (this.id = request[0].id),
            (this.specialInstructions = request[0].specialInstructions),
            (this.status = request[0].status),
            (this.orderDate = request[0].dateCreated),
            (this.orderDetails = request[0].purchaseOrders),
            (this.total = request[0].cost),
            (this.orderCompanyId =
                request[0].company && request[0].company.id != null
                    ? request[0].company.id
                    : null),
            (this.company = request[0].company.name),
            (this.companyCode = request[0].company.code),
            (this.billingCompanyName = request[0].company.plant.name),
            (this.billingStreet = request[0].company.plant.street),
            (this.billingStreet2 = request[0].company.plant.street2),
            (this.billingCity = request[0].company.plant.city),
            (this.billingState = request[0].company.plant.state),
            (this.billingPostalCode = request[0].company.plant.postalCode),
            (this.shippingCompanyName = request[0].company.shipTo.name),
            (this.shippingStreet = request[0].company.shipTo.street),
            (this.shippingStreet2 = request[0].company.shipTo.street2),
            (this.shippingCity = request[0].company.shipTo.city),
            (this.shippingState = request[0].company.shipTo.state),
            (this.shippingPostalCode = request[0].company.shipTo.postalCode);
        },
        viewOrderFailed(err) {
            this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
                title: err.error.response.data.title,
                autoHideDelay: 10000,
                appendToast: false,
                variant: "danger",
                solid: true
            });
        },
        reorder(id) {
            apiService
                .reorder({
                    id: id
                })
                .then(request => {
                    this.reorderSuccessful(request);
                })
                .catch(err => this.reorderFailed(err));
        },
        reorderSuccessful(request) {
            // Backend POST /cart/reorder/{id} returns the new cart id
            // as a raw integer (`return Ok(cart.Id)`), NOT a cart
            // object. Previously this method treated `request` as an
            // object and read `request.id` — which is undefined,
            // breaking the route param. localStorage.cartId still got
            // set correctly (request itself is the id), so the cart
            // page could load via getCart() lookup, but route params
            // arrived broken.
            //
            // The reorder flag still needs to flow through so cart.vue
            // can detect the reorder context and auto-open the Modify
            // Order modal for the user to assign PO Numbers (the
            // backend clears Numbers on reorder so PO-less orders
            // don't render in the grid until the user fills them in).
            localStorage.cartId = request;
            // Log the SUCCESS for the same-company Re-Order path (request IS the
            // new cart id; target = current company == the order's company on
            // this path). The once-guard no-ops when reorderForOrderCompany
            // already logged before calling here.
            this.logReorderSuccessEvent(localStorage.companyId || "", request);
            this.$router.push({
                name: "Cart",
                params: {
                    id: request,
                    reorder: true
                }
            });
        },
        reorderFailed(err) {
            // Log the FAILURE for the same-company Re-Order path (best-effort;
            // once-guard no-ops when reorderForOrderCompany already logged).
            this.logReorderFailureEvent(localStorage.companyId || "");
            this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
                title: err.error.response.data.title,
                autoHideDelay: 10000,
                appendToast: false,
                variant: "danger",
                solid: true
            });
        },

        // ===== COMPANY LIST LOADER (Feature 1) =====
        getCompanies() {
            apiService
                .getCompanyList()
                .then((data) => {
                    this.companies = data || [];
                })
                .catch(() => {
                    this.companies = [];
                });
        },

        // ===== RE-ORDER CLICK ROUTER (Feature 1) =====
        // Same-company (or unknown company) → unchanged reorder().
        // Different company → open the choice prompt. The order's
        // company id was captured into orderCompanyId in
        // viewOrderSuccessful (request[0].company.id).
        onReorderClick() {
            var currentCompanyId = localStorage.companyId || "";
            if (
                this.orderCompanyId == null ||
                String(this.orderCompanyId) === String(currentCompanyId)
            ) {
                // Reset the log once-guards so the success/failure event logging
                // in reorderSuccessful/reorderFailed fires for this reorder.
                this.reorderSuccessLogged = false;
                this.reorderFailLogged = false;
                this.reorder(this.id);
                return;
            }
            this.reorderMismatchBusy = false;
            this.resetReorderFailState();
            this.showReorderMismatchModal = true;
        },

        // Clears the "Add to my company" failure panel back to the choice
        // state. Called when the prompt (re)opens.
        resetReorderFailState() {
            this.reorderFailState = false;
            this.reorderFailReason = "";
            this.reorderMissing = [];
            this.reorderFailDiagnosing = false;
            this.reorderCanRetry = false;
            this.reorderFailLogged = false;
            this.reorderSuccessLogged = false;
        },

        // Choice 1 — "Add to [Order's Company]": the always-valid
        // existing /cart/reorder/{id} path (switches to the order's
        // company). reorderSuccessful handles the route.
        reorderForOrderCompany() {
            this.reorderMismatchBusy = true;
            var self = this;
            // The cart lands under the ORDER's own company — log the success/
            // failure against that company id.
            var orderCompanyId =
                this.orderCompanyId != null ? this.orderCompanyId : "";
            apiService
                .reorder({ id: this.id })
                .then(function (request) {
                    self.showReorderMismatchModal = false;
                    self.reorderMismatchBusy = false;
                    // request IS the raw new cart id. Log SUCCESS before routing.
                    self.logReorderSuccessEvent(orderCompanyId, request);
                    self.reorderSuccessful(request);
                })
                .catch(function (err) {
                    self.reorderMismatchBusy = false;
                    self.logReorderFailureEvent(orderCompanyId);
                    self.reorderFailed(err);
                });
        },

        // Choice 2 — "Add to my company": CROSS-COMPANY clone. Fetch
        // this order via reorderPreview(orderId) (ORDER repo — NOT getCart,
        // a CART id) and rebuild it as a NEW cart under the CURRENT company
        // via saveCart({ id:0, companyId }).
        // A fetched order's parts[] are CartPart wrappers — the real
        // part id is p.part.id || p.part.partId (NOT p.id); quantity
        // is p.quantity. PO Numbers cleared (number:"") so the user
        // assigns fresh POs in the Modify modal on /cart. On success
        // we set localStorage.cartId but leave localStorage.companyId
        // as the CURRENT company. On failure (parts not valid for this
        // company's machines) show a danger toast and don't navigate.
        reorderForMyCompany() {
            // Fresh attempt — clear any prior failure panel, then run.
            this.resetReorderFailState();
            this.runReorderForMyCompany();
        },

        // Re-run after the missing machine/wear parts were added.
        retryReorderForMyCompany() {
            this.reorderCanRetry = false;
            this.runReorderForMyCompany();
        },

        // Core cross-company clone. On failure, surface the REAL backend
        // error and diagnose what's missing (mirror of Account/Index.vue).
        runReorderForMyCompany() {
            var orderId = this.id;
            var currentCompanyId = Number(localStorage.companyId) || 0;
            this.reorderMismatchBusy = true;
            var self = this;
            // Clone the order into a NEW cart UNDER THE CURRENT COMPANY via the
            // SAME proven direct-create path the same-company reorder uses
            // (POST /cart/reorder/{id}) with ?companyId=<current>. The backend
            // builds the cart from the order's catalog line items WITHOUT the
            // Save action's model validation, avoiding the old immediate-saveCart
            // 400 (CartModel.CompanyId [Range] / blank-PO). Per-company machine
            // validation happens at the Modify-modal save on /cart. Returns the
            // new cart id as a RAW INTEGER (Ok(cart.Id)).
            apiService
                .reorder({ id: orderId, companyId: currentCompanyId })
                .then(function (newCartId) {
                    self.reorderMismatchBusy = false;
                    self.showReorderMismatchModal = false;
                    localStorage.cartId = newCartId;
                    // Record the cross-company SUCCESS with the full breakdown
                    // before navigating. Target = the CURRENT company.
                    self.logReorderSuccessEvent(currentCompanyId, newCartId);
                    self.$router.push({
                        name: "Cart",
                        params: { id: newCartId, reorder: true },
                    });
                })
                .catch(function (err) {
                    self.reorderMismatchBusy = false;
                    self.reorderFailReason = self.extractReorderError(err);
                    self.reorderFailState = true;
                    self.reorderCanRetry = false;
                    // RESTORE failure logging (the direct-create rewrite dropped
                    // it). Best-effort; reorderFailLogged keeps it to once.
                    self.logReorderFailureEvent(currentCompanyId);
                });
        },

        // Pulls the backend's actual error text out of the rejected axios
        // error (err.error.response.data.{title,detail,traceId}); falls
        // back to a bare-string body or a generic message.
        extractReorderError(err) {
            var data =
                err &&
                ((err.error && err.error.response && err.error.response.data) ||
                    (err.response && err.response.data) ||
                    err.data);
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && (data.title || data.detail)) {
                var msg = data.detail || data.title;
                if (data.title && data.detail && data.title !== data.detail) {
                    msg = data.title + " — " + data.detail;
                }
                if (data.traceId) msg += " (TraceID: " + data.traceId + ")";
                return msg;
            }
            var fallbackMsg =
                (err && err.message) ||
                (err && err.error && err.error.message) ||
                "";
            return (
                "The order couldn't be added to your company. " +
                (fallbackMsg
                    ? fallbackMsg
                    : "The parts/assemblies/machines on this order aren't set up under your company.")
            );
        },

        // Diagnoses which of the order's machines / assemblies / wear parts
        // aren't set up for the current company by fetching its account
        // setup (Master-only) and diffing the fetched order's POs. The
        // order's po.machineId / po.assemblyId are CATALOG ids; a fetched
        // part's real id is p.part.id — directly comparable to setup-summary.
        diagnoseMissingSetup(companyId, fetchedOrder) {
            var pos = (fetchedOrder && fetchedOrder.purchaseOrders) || [];
            if (!pos.length || !this.isMasterUser) {
                // No diagnosis possible (non-master, or no POs) — still log the
                // failure itself so it lands in Event Logs & History.
                this.logReorderFailureEvent(companyId);
                return;
            }
            var self = this;
            this.reorderFailDiagnosing = true;
            apiService
                .getAccountSetup(companyId)
                .then(function (setup) {
                    self.reorderMissing = self.computeMissing(setup, pos);
                    self.reorderFailDiagnosing = false;
                    // Log after the missing list is computed so it's included.
                    self.logReorderFailureEvent(companyId);
                })
                .catch(function () {
                    self.reorderFailDiagnosing = false;
                    self.logReorderFailureEvent(companyId);
                });
        },

        // Best-effort: record the cross-company reorder FAILURE into Account
        // Setup → Event Logs & History (POST /account/setup-event) so the
        // failure itself is visible there, not just the later remediation/add.
        // Logs at most once per failure (reorderFailLogged guard). Captures the
        // current company, the order + its placing company, the missing item(s)
        // with suggested fix, and the real backend error. Master-gated on the
        // server; non-masters' calls are ignored — we never await or surface it.
        logReorderFailureEvent(currentCompanyId) {
            if (this.reorderFailLogged) {
                return;
            }
            this.reorderFailLogged = true;
            try {
                var missing = this.reorderMissing || [];
                var missingLabels = missing.map(function (m) {
                    return (m.kindLabel ? m.kindLabel + ": " : "") + (m.label || "");
                });
                var suggestions = missing
                    .map(function (m) {
                        return m.fix || "";
                    })
                    .filter(function (s) {
                        return !!s;
                    });
                var detailParts = [
                    "Cross-company reorder failed.",
                    "Order #" + (this.id != null ? this.id : "?") +
                        " was placed by " +
                        this.orderCompanyDisplay +
                        ".",
                    "Attempted to add it to " +
                        this.currentCompanyDisplayLabel +
                        ".",
                ];
                if (missingLabels.length) {
                    detailParts.push("Missing: " + missingLabels.join("; ") + ".");
                }
                if (this.reorderFailReason) {
                    detailParts.push("Backend error: " + this.reorderFailReason);
                }

                var data = {
                    currentCompanyId: String(currentCompanyId || ""),
                    orderId: String(this.id != null ? this.id : ""),
                    orderCompanyId: String(
                        this.orderCompanyId != null ? this.orderCompanyId : ""
                    ),
                    orderCompanyName: this.company || "",
                    orderCompanyCode: this.companyCode || "",
                    missingCount: String(missing.length),
                };
                if (missingLabels.length) {
                    data.missingItems = missingLabels.join("; ");
                }

                apiService
                    .recordSetupEvent({
                        kind: "reorder-cross-company-failed",
                        title: "Cross-company reorder failed",
                        detail: detailParts.join(" "),
                        companyId: Number(currentCompanyId) || 0,
                        company: this.currentCompanyDisplayLabel,
                        success: false,
                        exception: this.reorderFailReason || "",
                        suggestions: suggestions,
                        data: data,
                    })
                    .catch(function () {
                        /* best-effort: logging must never block or break the UI */
                    });
            } catch (e) {
                /* swallow — diagnostics logging is non-critical */
            }
        },

        // ===== REORDER BREAKDOWN BUILDER (Issue A) =====
        // Builds a full line-item breakdown from the order data already loaded
        // into this component by viewOrderSuccessful: this.orderDetails (the
        // order's purchaseOrders[], each { purchaseOrderNumber, parts[] } with
        // parts[] = { partCode, partItem, partDescription, partQuantity,
        // partPrice, partQuantityPerMachine }) and this.total (the order's
        // stored cost). Same shape/output as Account/Index.vue's builder — no
        // backend call. line = qty × price; orderTotal prefers this.total and
        // falls back to the summed line totals.
        buildReorderBreakdown() {
            var pos = this.orderDetails || [];
            var money = function (n) {
                var v = Number(n);
                if (!isFinite(v)) v = 0;
                return v.toFixed(2);
            };
            var parts = [];
            var poNumbers = [];
            var seenPo = {};
            var lineSum = 0;
            pos.forEach(function (po) {
                var poNum = po && po.purchaseOrderNumber;
                if (poNum && !seenPo[poNum]) {
                    seenPo[poNum] = true;
                    poNumbers.push(poNum);
                }
                (po && po.parts ? po.parts : []).forEach(function (p) {
                    var code = p.partCode || p.partItem || p.partDescription || "Part";
                    var qty = Number(p.partQuantity) || 0;
                    var price = Number(p.partPrice) || 0;
                    var line = qty * price;
                    lineSum += line;
                    parts.push({ code: code, qty: qty, price: price, line: line });
                });
            });
            var orderTotal =
                this.total != null && isFinite(Number(this.total))
                    ? Number(this.total)
                    : lineSum;
            var partsLine = parts
                .map(function (p) {
                    return (
                        p.code + " ×" + p.qty + " @ $" + money(p.price) +
                        " = $" + money(p.line)
                    );
                })
                .join("; ");
            return {
                partCount: parts.length,
                orderTotal: orderTotal,
                orderTotalText: money(orderTotal),
                poNumbers: poNumbers.join(", "),
                partsLine: partsLine,
                parts: parts,
            };
        },

        // ===== REORDER SUCCESS EVENT (Issue A) =====
        // Best-effort: record a SUCCESS event into Account Setup → Event Logs &
        // History (same endpoint/shape as logReorderFailureEvent, success:true)
        // whenever a reorder completes from this page. Fired from all three
        // reorder entry points + retry; reorderSuccessLogged keeps it to one
        // event per reorder. Master-gated server-side; non-masters' calls are
        // ignored — we never await or surface any error.
        logReorderSuccessEvent(targetCompanyId, newCartId) {
            if (this.reorderSuccessLogged) {
                return;
            }
            this.reorderSuccessLogged = true;
            try {
                var b = this.buildReorderBreakdown();
                var crossCompany =
                    this.orderCompanyId != null &&
                    String(this.orderCompanyId) !== String(targetCompanyId || "");
                // The cart landed under targetCompanyId — the order's own
                // company (same-company / "add to order's company") or the
                // current company (cross-company "add to my company"). Name the
                // company the cart was actually created under.
                var targetCompanyDisplay =
                    this.orderCompanyId != null &&
                    String(targetCompanyId || "") === String(this.orderCompanyId)
                        ? this.orderCompanyDisplay
                        : this.currentCompanyDisplayLabel;

                var detail =
                    "Reordered Order #" +
                    (this.id != null ? this.id : "?") +
                    " (placed by " +
                    this.orderCompanyDisplay +
                    ") into " +
                    targetCompanyDisplay +
                    ". New cart #" +
                    (newCartId != null ? newCartId : "?") +
                    ". " +
                    b.partCount +
                    (b.partCount === 1 ? " part" : " parts") +
                    ", total $" +
                    b.orderTotalText +
                    ".";

                var data = {
                    orderId: String(this.id != null ? this.id : ""),
                    orderCompanyId: String(
                        this.orderCompanyId != null ? this.orderCompanyId : ""
                    ),
                    orderCompanyName: this.company || "",
                    orderCompanyCode: this.companyCode || "",
                    targetCompanyId: String(targetCompanyId || ""),
                    newCartId: String(newCartId != null ? newCartId : ""),
                    orderTotal: b.orderTotalText,
                    partCount: String(b.partCount),
                    parts: b.partsLine,
                    poNumbers: b.poNumbers,
                };

                apiService
                    .recordSetupEvent({
                        kind: crossCompany
                            ? "reorder-cross-company-success"
                            : "reorder-success",
                        title: crossCompany
                            ? "Cross-company reorder succeeded"
                            : "Reorder succeeded",
                        detail: detail,
                        companyId: Number(targetCompanyId) || 0,
                        company: targetCompanyDisplay,
                        success: true,
                        exception: "",
                        suggestions: [],
                        data: data,
                    })
                    .catch(function () {
                        /* best-effort: logging must never block or break the UI */
                    });
            } catch (e) {
                /* swallow — diagnostics logging is non-critical */
            }
        },

        // Builds the missing-items list from a setup-summary payload + the
        // order's POs. A missing machine supersedes its assembly/wear
        // misses (add the machine first). Mirror of Account/Index.vue.
        computeMissing(setup, pos) {
            var idsOf = function (cat) {
                return new Set(
                    ((cat && cat.items) || []).map(function (it) {
                        return String(it.id);
                    })
                );
            };
            var haveMachines = idsOf(setup && setup.machines);
            var haveAssemblies = new Set([
                ...idsOf(setup && setup.assemblies),
                ...idsOf(setup && setup.subAssemblies),
            ]);
            // dbo.Wear is keyed by (CompanyId, MachineId, AssemblyId, PartId), so the
            // SAME part can be wear under several machines/assemblies. Match the
            // order's parts on the FULL (machineId, assemblyId, partId) tuple — not
            // partId alone, which wrongly treated a part already linked as wear under
            // ANY machine as present and missed the case where the order needs that
            // part under a DIFFERENT machine+assembly the company has no Wear row for.
            // setup.wearParts[].machineId / .assemblyId now carry the numeric ids.
            var tupleKey = function (mId, aId, pId) {
                return String(mId || 0) + "|" + String(aId || 0) + "|" + String(pId || 0);
            };
            var haveWearTuples = new Set(
                ((setup && setup.wearParts && setup.wearParts.items) || []).map(function (
                    it
                ) {
                    return tupleKey(it.machineId, it.assemblyId, it.id);
                })
            );

            var missing = [];
            var seen = {};
            var push = function (
                key,
                kind,
                kindLabel,
                label,
                fix,
                refId,
                partId,
                machineId,
                assemblyId
            ) {
                if (seen[key]) return;
                seen[key] = true;
                missing.push({
                    key: key,
                    kind: kind,
                    kindLabel: kindLabel,
                    label: label,
                    fix: fix,
                    refId: refId || 0,
                    partId: partId || 0,
                    // Carried for wear adds so the "Add" action can create the EXACT
                    // dbo.Wear row the order needs (machine+assembly+part). 0 otherwise.
                    machineId: machineId || 0,
                    assemblyId: assemblyId || 0,
                });
            };
            var companyName = this.currentCompanyNameLabel;

            pos.forEach(function (po) {
                var mId = po.machineId;
                var machineMissing = mId != null && !haveMachines.has(String(mId));
                if (machineMissing) {
                    var mLabel = po.machine
                        ? (po.machine.code ? po.machine.code + " — " : "") +
                          (po.machine.description || po.machine.name || "Machine " + mId)
                        : "Machine " + mId;
                    push(
                        "m" + mId,
                        "machine",
                        "Machine",
                        mLabel,
                        "Add machine “" + mLabel + "” to " + companyName + ".",
                        mId,
                        0,
                        0,
                        0
                    );
                    return;
                }
                var aId = po.assemblyId;
                if (aId != null && !haveAssemblies.has(String(aId))) {
                    var aLabel = po.assembly
                        ? po.assembly.name || "Assembly " + aId
                        : "Assembly " + aId;
                    push(
                        "a" + aId,
                        "assembly",
                        "Assembly",
                        aLabel,
                        "Assembly “" +
                            aLabel +
                            "” isn't active for " +
                            companyName +
                            " — an admin must enable it in the Admin area (it can't be added from here).",
                        aId,
                        0,
                        0,
                        0
                    );
                }
                // Wear: flag any ordered part whose (machineId, assemblyId, partId)
                // tuple is NOT in this company's wear list. Machine+assembly are
                // present (checked above), so a missing tuple is the add-able wear
                // case. Keyed by the tuple so the same part missing under a second
                // machine is listed (and added) separately. Mirror of Account/Index.vue.
                var mName = po.machine
                    ? (po.machine.code ? po.machine.code + " — " : "") +
                      (po.machine.description || po.machine.name || "Machine " + mId)
                    : "Machine " + mId;
                var aName = po.assembly
                    ? po.assembly.name || "Assembly " + (po.assemblyId || "")
                    : "Assembly " + (po.assemblyId || "");
                (po.parts || []).forEach(function (p) {
                    var pid = (p.part && (p.part.id || p.part.partId)) || p.partId;
                    if (pid == null) return;
                    if (!haveWearTuples.has(tupleKey(mId, aId, pid))) {
                        var pLabel =
                            p.part && (p.part.code || p.part.item || p.part.description)
                                ? (p.part.code || p.part.item) +
                                  (p.part.description ? " — " + p.part.description : "")
                                : "Part " + pid;
                        push(
                            "w" + mId + "-" + aId + "-" + pid,
                            "wear",
                            "Wear part",
                            pLabel + " (on " + mName + " · " + aName + ")",
                            "Add wear part “" +
                                pLabel +
                                "” on " +
                                mName +
                                " to " +
                                companyName +
                                ".",
                            pid,
                            pid,
                            mId,
                            aId
                        );
                    }
                });
            });
            return missing;
        },

        // Adds the add-able missing items (machines + wear parts) via
        // setup-link-add, then offers a re-try. Each successful add is
        // recorded by the backend into the setup change history, so it
        // also appears in Account Setup → Event Logs & History.
        //
        // temporary === true  → "Add just this time": same add, but each
        //                        successful add is also recorded client-side
        //                        (addTempSetupAdds) so it can be auto-removed
        //                        when this order is placed / quote is finalized.
        //                        The add itself is IDENTICAL to the permanent
        //                        path — the only difference is the recording.
        // temporary === false → "Add for future orders & quotes": permanent;
        //                        behavior is byte-for-byte the original.
        addMissingToMyCompany(temporary) {
            var companyId = localStorage.companyId || "";
            var items = this.addableMissing;
            if (!companyId || !items.length) return;
            var self = this;
            var cid = Number(companyId);
            // Undo entries for the items that actually succeeded (temp path).
            var tempEntries = [];
            this.reorderMismatchBusy = true;
            var chain = Promise.resolve();
            items.forEach(function (m) {
                chain = chain.then(function () {
                    var payload = {
                        companyId: cid,
                        type: m.kind, // "machine" | "wear"
                        refId: m.refId,
                        category: m.kind === "wear" ? "wearParts" : "machines",
                    };
                    // For wear, target the EXACT machine+assembly the order needs so
                    // the backend inserts the precise dbo.Wear row (not one re-derived
                    // from the part's catalog assembly, which lands on the wrong
                    // machine when the part spans several). Mirror of Account/Index.vue.
                    if (m.kind === "wear" && m.machineId) {
                        payload.machineId = m.machineId;
                        payload.assemblyId = m.assemblyId || 0;
                    }
                    return apiService.addSetupLink(payload).then(function () {
                        // Record the undo payload only AFTER this add succeeded, so a
                        // later failure can't strand a phantom entry. Mirrors the add
                        // payload exactly + the item's label for precise cleanup.
                        if (temporary === true) {
                            tempEntries.push({
                                companyId: cid,
                                type: payload.type,
                                refId: payload.refId,
                                machineId: payload.machineId || 0,
                                assemblyId: payload.assemblyId || 0,
                                label: m.label || "",
                                category: payload.category,
                            });
                        }
                    });
                });
            });
            chain
                .then(function () {
                    self.reorderMismatchBusy = false;
                    if (temporary === true && tempEntries.length) {
                        addTempSetupAdds(cid, tempEntries);
                    }
                    self.reorderMissing = [];
                    self.reorderCanRetry = true;
                    self.$bvToast.toast(
                        "Added " +
                            self.addableMissingSummaryStatic(items) +
                            " to " +
                            self.currentCompanyNameLabel +
                            (temporary === true
                                ? " just for this re-order (auto-removed after it's placed / the quote is finalized)."
                                : ".") +
                            " Re-try the re-order to finish.",
                        {
                            title: "Setup updated",
                            autoHideDelay: 8000,
                            appendToast: false,
                            variant: "success",
                            solid: true,
                        }
                    );
                })
                .catch(function (err) {
                    self.reorderMismatchBusy = false;
                    // Persist any temp adds that DID succeed before the chain failed,
                    // so those real backend rows still get auto-removed later.
                    if (temporary === true && tempEntries.length) {
                        addTempSetupAdds(cid, tempEntries);
                    }
                    self.$bvToast.toast(self.extractReorderError(err), {
                        title: "Couldn't add the missing items",
                        autoHideDelay: 10000,
                        appendToast: false,
                        variant: "danger",
                        solid: true,
                    });
                });
        },

        // Summary string for a given items list (toast text after adding).
        addableMissingSummaryStatic(items) {
            var machines = items.filter(function (m) {
                return m.kind === "machine";
            }).length;
            var wears = items.filter(function (m) {
                return m.kind === "wear";
            }).length;
            var parts = [];
            if (machines)
                parts.push(machines + (machines > 1 ? " machines" : " machine"));
            if (wears)
                parts.push(wears + (wears > 1 ? " wear parts" : " wear part"));
            return parts.join(" + ") || "the missing items";
        },
        showDeleteModal(id) {
            this.$bvModal
                .msgBoxConfirm(
                    "PLEASE CONFIRM YOU WANT TO DELETE ENTIRE ORDER",
                    {
                        title: "DELETE ENTIRE ORDER?",
                        id: "deleteModal",
                        size: "md",
                        buttonSize: "sm",
                        okVariant: "dark",
                        okTitle: "Yes, please delete",
                        cancelVariant: "outline",
                        cancelTitle: "No, please don’t",
                        hideHeaderClose: true,
                        centered: true
                    }
                )
                .then(clear => {
                    if (clear && id) {
                        apiService
                            .deleteOrder({ id: id })
                            .then(request => { this.deleteSuccessful(request); })
                            .catch(err => this.deleteFailed(err));
                    }
                })
                .catch(err => this.deleteFailed(err));
        },
        deleteSuccessful() {
            this.$router.push({ name: "OrderHistory" });
        },
        deleteFailed(err) {
            this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
                title: err.error.response.data.title,
                autoHideDelay: 10000,
                appendToast: false,
                variant: "danger",
                solid: true
            });
        }
    }
};
</script>

<style scoped>
/* ===== PAGE CONTAINER =====
   position: relative gives the section its own stacking context so
   nothing inside accidentally floats above the fixed-position side
   rails (body::before / body::after at z-index 999). */
#orderDetails {
    position: relative;
}

/* ===== PAGE HEADER (Order # + Date + Status) ===== */
.order-page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    padding-bottom: 16px;
    margin-bottom: 24px;
    border-bottom: 1px dashed #d8d8d8;
}

.order-page-header-main h1 {
    margin: 0 0 4px;
    font-size: 26px;
    font-weight: 700;
    color: #222;
    letter-spacing: 0.2px;
}

.order-page-header-meta {
    margin: 0;
    font-size: 13px;
    color: #777;
}

.order-page-header-status {
    flex-shrink: 0;
}

/* ===== STATUS BADGE =====
   Pill-shaped, color-coded by status string. Lowercased class binding
   in the template means any casing of "Pending" maps to .pending.
   Unknown statuses fall through to the base rule (neutral gray). */
.order-status-badge {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    background: #eee;
    color: #555;
    border: 1px solid #ddd;
}

.order-status-badge.pending {
    background: #fff3cd;
    color: #856404;
    border-color: #ffeaa7;
}

.order-status-badge.shipped {
    background: #d4edda;
    color: #155724;
    border-color: #c3e6cb;
}

.order-status-badge.complete {
    background: #d1ecf1;
    color: #0c5460;
    border-color: #bee5eb;
}

.order-status-badge.cancelled,
.order-status-badge.canceled {
    background: #f8d7da;
    color: #721c24;
    border-color: #f5c6cb;
}

/* ===== INFO CARDS (Sold To + Ship To) ===== */
.info-cards-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 28px;
}

.info-card {
    flex: 1 1 0;
    min-width: 240px;
    background: #fff;
    border: 1px solid #ececec;
    border-top: 3px solid #c0392b;
    border-radius: 6px;
    padding: 16px 20px 14px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.info-card-header {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #c0392b;
    padding-bottom: 8px;
    margin-bottom: 10px;
    border-bottom: 1px dashed #d8d8d8;
}

.info-card-body {
    font-size: 13px;
    color: #555;
    line-height: 1.5;
}

.info-card-name {
    font-size: 15px;
    font-weight: 700;
    color: #222;
    margin-bottom: 6px;
}

.info-card-body p {
    margin: 0 0 2px;
}

.info-card-meta {
    margin-top: 10px !important;
    font-size: 12px !important;
    color: #888 !important;
    font-style: italic;
}

/* ===== SPECIAL INSTRUCTIONS BLOCK ===== */
.special-instructions-block {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #888;
    border-radius: 6px;
    padding: 14px 18px;
    margin-bottom: 28px;
}

.special-instructions-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #555;
    margin-bottom: 6px;
}

.special-instructions-text {
    font-size: 13px;
    color: #444;
    line-height: 1.5;
    white-space: pre-wrap;
}

/* ===== PO GROUP (the grouping container) ===== */
.po-group {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #c0392b;
    border-radius: 8px;
    margin: 0 0 28px;
    padding: 24px 22px 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.order-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #d8d8d8;
}

.order-header-label {
    font-size: 20px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.order-header-meta {
    font-size: 12px;
    color: #777;
    font-style: italic;
}

/* ===== ASSEMBLY CARD ===== */
.assembly-card {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 6px;
    padding: 18px 20px 14px;
    margin-bottom: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.assembly-card:last-of-type {
    margin-bottom: 18px;
}

.assembly-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.assembly-card-heading {
    flex: 1 1 auto;
    min-width: 200px;
}

.assembly-card-po-subtotal {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 8px 14px;
}

.assembly-card-po-label {
    font-size: 12px;
    color: #555;
    white-space: nowrap;
}

.assembly-card-po-amount {
    font-size: 16px;
    font-weight: 700;
    color: #c0392b;
    white-space: nowrap;
    letter-spacing: 0.3px;
}

@media (max-width: 767.98px) {
    .assembly-card-header {
        flex-direction: column;
    }
    .assembly-card-po-subtotal {
        align-self: stretch;
        justify-content: space-between;
    }
}

.assemblyName {
    font-size: 20px;
    font-weight: 700;
    color: #333;
    margin-bottom: 2px;
}

.assemblyDescription {
    font-size: 13px;
    color: #777;
}

.subtotal-po {
    color: #c0392b;
    font-weight: 700;
}

/* ===== COLLAPSIBLE ASSEMBLY SUMMARY ROW ===== */
.assembly-summary {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 12px 10px 10px;
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 6px;
    cursor: pointer;
    margin-top: 4px;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.assembly-summary:hover {
    background: #f3f3f3;
    border-color: #e0e0e0;
}

.assembly-summary.expanded {
    background: #fff;
    border-color: #ddd;
}

.assembly-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    border: 1.5px solid #c0392b;
    background: #fff;
    color: #c0392b;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.assembly-summary:hover .assembly-toggle {
    background: #c0392b;
    color: #fff;
}

.assembly-toggle.expanded {
    background: #333;
    border-color: #333;
    color: #fff;
}

.assembly-summary:hover .assembly-toggle.expanded {
    background: #555;
    border-color: #555;
}

.assembly-toggle-icon {
    display: block;
    line-height: 1;
    margin-top: -2px;
}

.assembly-summary-content {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.assembly-summary-count {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #888;
    margin-right: 4px;
}

.code-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

.code-chip .code-qty {
    color: #c0392b;
    font-weight: 700;
    font-size: 12px;
}

.assembly-summary-empty {
    font-size: 13px;
    color: #999;
    font-style: italic;
}

.assembly-summary-total {
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 700;
    color: #c0392b;
    min-width: 100px;
    text-align: right;
}

.assembly-table-wrap {
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}
/* The per-table Ordered/Cost totals line is print-only. */
.parts-total-line {
    display: none;
}
/* The "N Parts at" prefix on the grand total is print-only; on screen
   the grand total stays just the dollar amount. */
.grand-total-parts-prefix {
    display: none;
}

/* Screen: the parts table spans the full content width (mirrors the print
   fix further down) so its columns spread out and align with the headers
   instead of squishing into the left ~60%. The Description column (3rd)
   absorbs the slack; the rest size to their content. Scoped to >= lg so it
   doesn't fight the stacked mobile layout. */
.assembly-table-wrap table {
    width: 100% !important;
    table-layout: auto !important;
}
/* Parts table body cells: explicit dark text so the values read clearly on
   the white (light-mode) panel. Dark/Mid modes override this in
   darkmode.css / mid-mode.css; the @media print block below overrides it to
   pure black. The header band is styled separately and is left untouched. */
.assembly-table-wrap td {
    color: #212529 !important;
}
@media (min-width: 992px) {
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        white-space: nowrap;
    }
    .assembly-table-wrap th:nth-child(3),
    .assembly-table-wrap td:nth-child(3) {
        white-space: normal;
        width: 99%;
    }
}

/* ===== ORDER GRAND TOTAL =====
   Direct port of Cart's cart-grand-total: heavy black top border,
   centered uppercase label, large red amount. */
.cart-grand-total {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 18px 20px;
    margin-top: 8px;
    margin-bottom: 24px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-top: 3px solid #222;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cart-grand-total-label {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #333;
}

.cart-grand-total-value {
    font-size: 22px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

@media (max-width: 575.98px) {
    .cart-grand-total {
        padding: 14px 16px;
    }
    .cart-grand-total-value {
        font-size: 18px;
    }
}

/* ===== FINAL ACTIONS — Re-Order button =====
   Same vertical position and visual treatment as Cart's Place Order
   button: full-width red primary button, bold uppercase letter spacing.
   Disclaimer beneath sets price/availability expectations since the
   order being viewed could be months old. */
#finalActions {
    padding: 24px 0 32px;
}

.btn-place-order {
    display: block;
    width: 100%;
    text-align: center;
    padding: 14px 20px;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.reorder-disclaimer {
    margin: 12px 0 0;
    text-align: center;
    font-size: 12px;
    color: #888;
    font-style: italic;
}

/* ===== PRINT-ONLY HEADER (HIDDEN ON SCREEN) =====
   The letterhead-style banner that sits above each order
   group in the v-for appears ONLY in print output. Hidden
   by default here so it doesn't show in the live UI;
   revealed by the @media print rule in the non-scoped
   style block at the bottom of this file. */
.print-only-header {
    display: none;
}

/* ===== PRINT ORDER BAND =====
   Centered Print button in its own breathing-room band
   between the page header and the order detail blocks.
   no-print on the wrapper so it doesn't appear in the
   printed receipt. */
.print-order-band {
    padding: 16px 0 4px;
    text-align: center;
    background: transparent;
}

/* ===== PRINT ORDER BUTTON =====
   Default state: brand red background with white text so
   the action reads as a primary CTA on this page. Hover
   transitions to black for a clear pressed-state cue.
   The brand red (#c0392b) matches the modal banner border
   and the per-PO subtotal callouts elsewhere on the page,
   keeping the Print action visually anchored to the same
   color vocabulary as the rest of the order pages. */
.btn-print-order {
    display: inline-flex;
    /* Keep the button content-width and centered in the band — not
       stretched across the page. */
    width: auto;
    max-width: 280px;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
    gap: 8px;
    padding: 10px 22px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.3px;
    border: 1.5px solid #c0392b;
    border-radius: 6px;
    background: #c0392b;
    color: #fff;
    cursor: pointer;
    transition: background 200ms ease, color 200ms ease,
                border-color 200ms ease, box-shadow 200ms ease;
}

.btn-print-order:hover,
.btn-print-order:focus {
    background: #000;
    color: #fff;
    border-color: #000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.btn-print-icon {
    display: inline-block;
}

/* ===== PER-ORDER PRINT SECTION =====
   Wrapper around each order group that gives the @media
   print rules a hook to (a) page-break between consecutive
   orders and (b) hide a section entirely when the user
   picked a subset to print. On screen it's a transparent
   passthrough — no padding, no border, no visual change. */
.print-order-section {
    /* Intentionally empty on screen — display:block default
       with no styling. Print rules in the non-scoped style
       block below do all the work. */
}

/* ===== PRINT ORDER MODAL =====
   Modal-scoped styles for #print-order-modal, mirroring the
   visual treatment of the Print Quote / Finalize modals on
   the quote pages so the Order and Quote flows feel like
   siblings. The .modify-restored-banner / .convert-* class
   names are reused verbatim so any future tweaks to the
   picker styling there propagate here without divergence —
   the styles below scope them to .print-order-modal so they
   don't leak globally. */
.print-order-modal .modify-restored-banner.print-order-banner {
    background: linear-gradient(135deg, #fff5f3 0%, #fff 100%);
    border: 2px solid #c0392b;
    border-radius: 10px;
    padding: 18px 22px;
    margin: 0 0 18px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(192, 57, 43, 0.12),
                0 1px 3px rgba(0, 0, 0, 0.04);
}

.print-order-modal .modify-restored-title {
    font-size: 20px;
    font-weight: 800;
    color: #c0392b;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
    line-height: 1.2;
}

.print-order-modal .modify-restored-body {
    font-size: 13px;
    color: #444;
    line-height: 1.5;
}

.print-order-modal .modify-restored-body strong {
    color: #222;
    font-weight: 700;
}

@media (max-width: 575.98px) {
    .print-order-modal .modify-restored-title {
        font-size: 18px;
    }
    .print-order-modal .modify-restored-banner.print-order-banner {
        padding: 14px 16px;
    }
}

.print-order-modal .convert-modal-intro {
    margin: 0 0 14px;
    font-size: 13px;
    color: #555;
}

.print-order-modal .convert-quote-list {
    margin: 0 0 12px;
    padding: 0;
}

.print-order-modal .convert-quote-row {
    padding: 10px 12px;
    background: #fafafa;
    border: 1px solid #ececec;
    border-radius: 6px;
    margin-bottom: 6px;
}

.print-order-modal .convert-quote-row:last-child {
    margin-bottom: 0;
}

.print-order-modal .convert-quote-row-grouped {
    border-left: 3px solid #c0392b;
}

.print-order-modal .convert-quote-row .custom-control-label {
    cursor: pointer;
    width: 100%;
}

.print-order-modal .convert-quote-row-main {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 2px;
}

.print-order-modal .convert-quote-row-label {
    font-size: 14px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.print-order-modal .convert-quote-row-meta {
    font-size: 12px;
    color: #555;
    font-weight: 400;
}

.print-order-modal .convert-quote-row-meta strong {
    color: #222;
    font-weight: 700;
}

.print-order-modal .convert-quote-row-sub {
    font-size: 11px;
    color: #888;
    letter-spacing: 0.2px;
    text-transform: uppercase;
}

.print-order-modal .convert-quote-row-amount {
    color: #c0392b;
    font-weight: 700;
}

.print-order-modal .convert-quote-row-assemblies {
    margin-top: 4px;
    font-size: 11px;
    color: #666;
    line-height: 1.4;
}

.print-order-modal .convert-quote-row-assembly {
    padding: 1px 0;
}

.print-order-modal .print-order-row-static {
    padding: 0;
}

.print-order-modal .convert-quote-toggle-all {
    text-align: right;
    margin: 0 0 4px;
}

.print-order-modal .convert-quote-toggle-all a {
    font-size: 11px;
    font-weight: 700;
    color: #c0392b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    text-decoration: none;
}

.print-order-modal .convert-quote-toggle-all a:hover {
    text-decoration: underline;
}
</style>

<!-- ============================================================
     PRINT STYLESHEET (non-scoped, global @media print)
     ============================================================
     Same pattern as SavedRFQ.vue / RFQDetails.vue / Success.vue.
     Hides page chrome (header, breadcrumbs, footer, nav),
     #odTop (Sold To / Ship To info now lives elsewhere — the
     letterhead carries Parts Department info), the Re-Order
     button, and the Print band itself. Reveals each
     per-order letterhead. Forces every assembly's parts
     table expanded regardless of the user's collapse state,
     so the printed receipt shows all line items. Tightens
     typography for density. Inserts page-break-before
     between consecutive .print-order-section blocks so each
     Order # gets its own page with its own letterhead. -->
<style>
@media print {
    /* Hide chrome — multiple selector forms with elevated
       specificity to win against any global stylesheet
       rules with !important on these elements. */
    body #breadcrumb,
    body #breadcrumb *,
    div#breadcrumb,
    div#breadcrumb *,
    [id="breadcrumb"],
    [id="breadcrumb"] *,
    [class*="breadcrumb" i],
    [class*="breadcrumb" i] *,
    [id*="breadcrumb" i],
    [id*="breadcrumb" i] *,
    nav[aria-label*="breadcrumb" i],
    nav[aria-label*="Breadcrumb"],
    ol.breadcrumb,
    ul.breadcrumb,
    body header,
    body .header,
    body #header,
    body [role="banner"],
    body nav.navbar,
    body .navbar,
    body footer,
    body .footer,
    body #footer,
    /* Hide the on-screen page-header pieces (Order # + status, Sold-To /
       Ship-To info cards, Special Instructions) — the per-PO print
       letterhead carries the equivalent info in print. These used to be
       hidden via `body #odTop`, but #OrderTable now lives INSIDE
       #odTop's content card (single-card consolidation), so hiding the
       whole #odTop would blank the printed order. Target the header
       blocks specifically and leave #OrderTable visible. */
    body #odTop > .container > .order-page-header,
    body #odTop > .container > .info-cards-row,
    body #odTop > .container > .special-instructions-block,
    body #finalActions,
    .no-print,
    [class*="userway"],
    [id*="userway"],
    [class*="accessibility-widget"],
    [class*="translate-widget"],
    [id*="google_translate"],
    iframe {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Strip the brushed-steel body background. */
    html,
    body {
        background: #fff !important;
        background-image: none !important;
    }

    /* ===== STRIP TOP-OF-PAGE WHITESPACE =====
       App.vue sets a dynamic padding-top on body to clear
       the fixed site header. That padding has no purpose
       in print (header is hidden) but the inline style
       stays and creates a tall blank band at page top.
       Force it to zero. */
    html,
    body,
    #app,
    #app > div,
    main,
    .main,
    [role="main"],
    .container-fluid,
    #orderDetails {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
    body[style] {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    /* ===== PRINTABLE RECEIPT SPANS THE FULL PAGE =====
       #OrderTable (tagged .jcs-print-doc, the print-only root) renders inside
       #odTop's content card after the single-card consolidation. That card is
       a Bootstrap .container, whose max-width (540/720/960px at the print
       breakpoint) squeezed the receipt into a narrow, left-aligned column
       once it moved inside. Neutralize the width + box chrome on the WHOLE
       ancestor chain so the printout owns the full @page width and is
       INDEPENDENT of however the on-screen layout nests or restyles
       .container — the receipt is anchored to its own .jcs-print-doc root,
       not the shared site container. (The ::before brand stripe that drew the
       card's red line is killed just below.) */
    #orderDetails,
    #orderDetails #odTop,
    #orderDetails #odTop > .container,
    #orderDetails .jcs-print-doc {
        width: 100% !important;
        max-width: 100% !important;
        flex: 0 0 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
    }
    #orderDetails #odTop > .container::before {
        display: none !important;
        content: none !important;
    }

    /* Show the print-only header. Hidden on screen by the
       scoped rule above; revealed in print here. */
    .print-only-header {
        display: flex !important;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0 5px 16px;
        margin: 0 0 16px;
        border-bottom: 2px solid #c0392b;
    }

    .print-only-header-logo {
        flex: 0 0 auto;
        margin-left: 5px;
    }

    .print-only-header-logo-img {
        width: 180px;
        max-width: 180px;
        height: auto;
        max-height: 80px;
        display: block;
    }

    /* Text fallback used when the SVG <img> fails to load. */
    .print-only-header-logo-text {
        font-size: 32px;
        font-weight: 900;
        letter-spacing: 1px;
        color: #c0392b;
        line-height: 1;
        font-family: Arial, Helvetica, sans-serif;
    }

    .print-only-header-customer {
        flex: 1 1 auto;
        align-self: center;
        text-align: center;
        padding: 0 16px;
        color: #222;
        min-width: 0;
    }
    .print-only-header-customer-name {
        font-weight: 700 !important;
        font-size: 16px;
        line-height: 1.2;
    }
    .print-only-header-customer-id {
        font-size: 13px;
        color: #555;
        margin-top: 2px;
    }
    .print-only-header-address {
        flex: 0 0 auto;
        text-align: right;
        font-size: 12px;
        line-height: 1.4;
        color: #222;
    }

    .print-only-header-address-name {
        font-weight: 700;
        font-size: 12px;
        margin-bottom: 2px;
    }

    /* Doc identifier line above the Parts Department block.
       Brand-red + larger weight so it reads as the headline
       of the printed page — recipient can tell at a glance
       which Order/Quote/Acknowledgement they're looking at.
       Color-adjust hint forces the red through browsers'
       default print color-strip (which the rest of this
       stylesheet already uses on red elements). */
    .print-only-header-doc-id {
        font-weight: 700;
        font-size: 14px;
        color: #c0392b;
        margin-bottom: 6px;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* ===== INPUT-AS-TEXT IN PRINT =====
       Disabled <input> elements (e.g. the locked CART QTY
       input on Success.vue's parts table) carry intrinsic
       browser-default widths that override table-cell
       sizing and push other columns out of alignment with
       the header row. Strip the input chrome and intrinsic
       width so the value renders inline as text — same
       width as the other plain-text cells in the column.
       Defensive on files that don't currently use inputs in
       tables; harmless if no inputs are present. */
    .assembly-table-wrap input[disabled],
    .assembly-table-wrap input[readonly],
    .POOrders input[disabled],
    .POOrders input[readonly],
    .success-qty,
    .success-qty input {
        border: 0 !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        width: auto !important;
        max-width: none !important;
        min-width: 0 !important;
        display: inline !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        color: inherit !important;
        font: inherit !important;
        box-shadow: none !important;
        outline: 0 !important;
    }

    /* ===== TIGHT TYPOGRAPHY FOR PRINT =====
       Same density as RFQDetails so an order receipt fits
       in as few pages as possible. */
    body {
        font-size: 12px !important;
        line-height: 1.4 !important;
    }
    #orderDetails h1 {
        font-size: 20px !important;
        margin: 0 0 4px !important;
    }
    .po-group {
        margin-top: 6px !important;
        margin-bottom: 6px !important;
        padding: 6px 8px !important;
    }
    .order-header {
        margin-bottom: 6px !important;
        padding-bottom: 6px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
    }
    /* PO # as a black rectangle chip with white text, on the left. */
    .order-header-label {
        font-size: 16px !important;
        font-weight: 700 !important;
        background: #000 !important;
        color: #fff !important;
        padding: 4px 12px !important;
        border-radius: 3px !important;
        letter-spacing: 0.3px !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    /* Item count on the right of the same row. */
    .order-header-meta {
        font-size: 14px !important;
        font-style: normal !important;
        color: #000 !important;
    }
    .assembly-card {
        margin-bottom: 4px !important;
        padding: 6px 8px !important;
    }
    .assemblyName {
        font-size: 14px !important;
        margin: 0 !important;
    }
    .assemblyDescription {
        font-size: 11px !important;
        margin: 0 !important;
    }
    .assembly-card-po-label {
        font-size: 11px !important;
    }
    .assembly-card-po-amount {
        font-size: 12px !important;
    }
    .assembly-summary {
        /* Redundant in print — the full parts table is force-expanded
           below, so hide the collapsed "X parts + chips" preview row. */
        display: none !important;
    }
    /* Per-assembly "Subtotal for PO Number: X" row — removed from print
       per request (the grand total + per-part costs already cover it). */
    .assembly-card-po-subtotal {
        display: none !important;
    }
    /* Per-table totals line below the table (NOT a table row): plain,
       right-aligned "Total: <qty> at <cost>" — bold label, red amount. */
    .parts-total-line {
        display: block !important;
        text-align: right !important;
        margin: 4px 2px 0 !important;
        font-size: 13px !important;
    }
    .parts-total-strong {
        font-weight: 700 !important;
    }
    .parts-total-amount {
        color: #c0392b !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    /* Order Total value → bold red (force the red through the browser's
       default print color-strip). */
    .cart-grand-total-value {
        color: #c0392b !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    .assembly-summary-count,
    .code-chip,
    .code-qty {
        font-size: 9px !important;
    }
    .assembly-summary-total {
        font-size: 10px !important;
    }
    .assembly-table-wrap {
        margin: 4px 0 0 !important;
    }
    .assembly-table-wrap table {
        font-size: 12px !important;
        /* Full content width + FIXED layout: under auto-layout the header
           cells and data cells drifted out of alignment and the columns
           squished into the left ~60%. Fixed layout makes every row share
           the same column widths (so headers line up with their values) and
           the table fills the page. */
        width: 100% !important;
        table-layout: fixed !important;
    }
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        padding: 5px 8px !important;
        font-size: 12px !important;
        white-space: normal !important;
        word-break: break-word;
        vertical-align: top;
        overflow: hidden;
    }
    /* Value cells (Price / Ordered / Cost etc.) in pure black — they
       inherited a washed-out gray body color. Chips/badges keep their own
       colors because those are set on inner spans. */
    .assembly-table-wrap td {
        color: #000 !important;
    }
    /* Description (3rd column) takes the bulk of the width; the six short
       columns split the remainder evenly under fixed layout. */
    .assembly-table-wrap th:nth-child(3),
    .assembly-table-wrap td:nth-child(3) {
        width: 34% !important;
    }
    .cart-grand-total {
        margin-top: 8px !important;
        padding: 8px 12px !important;
    }
    .cart-grand-total-label {
        font-size: 14px !important;
        font-weight: 700 !important;
        color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    /* Show the "N Parts at" prefix in print (hidden on screen). It sits
       inside .cart-grand-total-value so it inherits the red + bold. */
    .grand-total-parts-prefix {
        display: inline !important;
        color: #c0392b !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    .cart-grand-total-value {
        font-size: 14px !important;
    }

    /* Page break control — only on small atoms, not on the
       wide po-group (which can force whole-block page-shoves
       and leave white gaps). */
    .cart-grand-total,
    .assembly-card {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* ===== FORCE-EXPAND COLLAPSED ASSEMBLIES =====
       v-show writes inline display:none when collapsed;
       override with !important to show every parts table
       in print regardless of expand/collapse state. */
    .assembly-table-wrap {
        display: block !important;
    }
    .assembly-table-wrap[style*="display: none"],
    .assembly-table-wrap[style*="display:none"] {
        display: block !important;
    }
    /* Hide the +/− toggle in print since the section it
       controls is always visible now. */
    .assembly-toggle {
        display: none !important;
    }

    /* b-table stacked-mode override — print viewport trips
       the lg breakpoint and would stack each cell vertically. */
    .b-table.b-table-stacked-lg > tbody > tr,
    .b-table.b-table-stacked-md > tbody > tr,
    .b-table.b-table-stacked-sm > tbody > tr {
        display: table-row !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > td,
    .b-table.b-table-stacked-md > tbody > tr > td,
    .b-table.b-table-stacked-sm > tbody > tr > td {
        display: table-cell !important;
    }
    .b-table.b-table-stacked-lg > thead,
    .b-table.b-table-stacked-md > thead,
    .b-table.b-table-stacked-sm > thead {
        display: table-header-group !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-md > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-sm > tbody > tr > [data-label]::before {
        display: none !important;
    }

    /* ===== PER-ORDER PAGE BREAKS =====
       Each .print-order-section past the first gets a
       page-break-before so every Order # block starts on
       its own printed page with its own letterhead at the
       top. The first section inherits the page-top
       position naturally — no break needed there. */
    .print-order-section + .print-order-section {
        page-break-before: always;
        break-before: page;
    }

    .print-order-section {
        page-break-inside: auto;
        break-inside: auto;
    }

    /* ===== SELECTIVE HIDE FOR SUBSET PRINTING =====
       confirmPrint() flips printingSelectedOnly=true and
       Vue toggles `print-order-hidden` onto every section
       whose group.number isn't in selectedOrdersForPrint.
       The display: none here removes those sections from
       the printed output entirely — including their
       per-order letterhead — so a 3-of-5 print only emits
       the picked 3 with correct page breaks between them. */
    .print-order-hidden,
    .print-order-hidden * {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    @page {
        margin: 0.5in;
    }

    /* ===== FORCE DARK TEXT FOR PRINT READABILITY =====
       Browser print engines lighten text by default to save
       ink. Combined with the various screen-only light-gray
       colors (#555, #666, #777, #888, #aaa) sprinkled across
       the screen styles, the printed output ends up muted
       and hard to read. Force every text node to full black
       AND bump font-weight slightly so thin glyphs don't
       disappear at small print font sizes. */
    *,
    *::before,
    *::after {
        color: #000 !important;
    }

    body,
    body p,
    body span,
    body td,
    body th,
    body div {
        font-weight: 500 !important;
    }

    /* ===== AGGRESSIVE GLOBAL DECORATIVE RESET =====
       Earlier attempts to strip borders only on specific
       containers (.po-group, .assembly-card, etc.) didn't
       eliminate the vertical gray lines the user reported
       running down the body of printed pages. Edge browser
       in particular prints subtle borders by default —
       different browsers behave differently here.

       Brute-force approach: wipe ALL backgrounds, borders,
       and shadows on EVERY element AND every pseudo-element.
       The pseudo-element coverage matters — App.vue (or a
       global stylesheet) sets up `body::before` and
       `body::after` to render the brushed-steel side rails
       around the page, and a universal `*` selector by
       itself doesn't catch those. Without this rule the
       rails stay visible in print and produce vertical bars
       running the full page height.

       border-color: transparent (rather than border-width:
       0) preserves layout — elements that used borders for
       spacing keep their box geometry, only the visible
       line goes away. */
    *,
    *::before,
    *::after {
        background: transparent !important;
        background-color: transparent !important;
        background-image: none !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
    }

    /* Explicit body pseudo-element kill — App.vue's brushed-
       steel side rails attach here. Belt-and-suspenders with
       the universal rule above; some browsers need the
       targeted nudge to actually drop the rendered content. */
    body::before,
    body::after,
    html::before,
    html::after {
        display: none !important;
        content: none !important;
    }

    /* ===== HIDE FLOATING UTILITY TOOLBAR =====
       Head.vue mounts a fixed-position #utility-toggle-stack
       in the bottom-right of every page, containing three
       toggles: Report-a-Bug, font-size (Aa), and translate
       (globe). These are screen-only UI affordances; in a
       printed receipt they're noise. Hide the whole stack
       AND each individual toggle in case any are rendered
       outside the stack wrapper for any reason. */
    #utility-toggle-stack,
    #utility-toggle-stack *,
    .util-toggle,
    .util-toggle-bug,
    .util-toggle-font,
    .util-toggle-translate,
    .util-icon,
    .util-label,
    .util-close,
    .util-body,
    #google-translate-floating,
    #google_translate_element {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        width: 0 !important;
        overflow: hidden !important;
    }

    /* Hide any horizontal-rule elements which would otherwise
       print as visible divider lines. */
    hr {
        display: none !important;
    }

    /* ===== RESTORE INTENTIONAL CHROME =====
       Now re-establish the visual elements that DO belong
       in print:
         - Data table cell borders carry data structure
         - print-only-header gets its red bottom rule (the
           branded divider between header and content)
         - Red brand accents stay red */
    table,
    .assembly-table-wrap table {
        border-collapse: collapse !important;
    }
    th,
    td,
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        border: 1px solid #888 !important;
    }

    .print-only-header {
        border-bottom: 2px solid #c0392b !important;
    }

    /* Targeted color preservation on the red brand elements
       so they print in their declared color instead of
       Edge's default auto-strip. */
    .print-only-header,
    .print-only-header *,
    .print-only-header-logo-img,
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* Restore red on the brand wordmark fallback + any
       explicit red accents the page intentionally carries.
       Order matters — these rules must come after the
       universal `*` color override above. */
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        color: #c0392b !important;
    }
}

/* ===== Re-order mismatch prompt (Feature 1) =====
   Unscoped so it reaches the b-modal content, which BootstrapVue
   teleports to <body>. Mirrors the dashboard prompt styling. */
.reorder-mismatch-lead {
    font-size: 14px;
    font-weight: 700;
    color: #2a2a2a;
    margin-bottom: 10px;
}
.reorder-mismatch-facts {
    list-style: none;
    padding: 0;
    margin: 0 0 12px 0;
    font-size: 13px;
    color: #444;
}
.reorder-mismatch-facts li {
    padding: 3px 0;
}
.reorder-mismatch-facts strong {
    color: #c0392b;
}
.reorder-mismatch-help {
    font-size: 13px;
    font-weight: 600;
    color: #2a2a2a;
    margin-bottom: 10px;
}
.reorder-mismatch-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}
.reorder-mismatch-btn {
    width: 100%;
    font-weight: 600;
}
.reorder-mismatch-caveat {
    font-size: 11px;
    color: #888;
    line-height: 1.4;
    margin: 0;
}

/* ===== Re-order "Add to my company" FAILURE panel ===== */
.reorder-fail-title {
    font-size: 14px;
    font-weight: 700;
    color: #c0392b;
    margin-bottom: 10px;
}
.reorder-fail-reason {
    font-size: 13px;
    color: #333;
    background: #fdf3f2;
    border: 1px solid #f3c9c4;
    border-radius: 6px;
    padding: 8px 10px;
    line-height: 1.4;
    margin-bottom: 12px;
    white-space: pre-wrap;
    word-break: break-word;
}
.reorder-fail-diagnosing {
    font-size: 12px;
    color: #888;
    font-style: italic;
    margin-bottom: 12px;
}
.reorder-fail-subhead {
    font-size: 12px;
    font-weight: 700;
    color: #2a2a2a;
    margin-bottom: 6px;
}
.reorder-fail-list {
    list-style: none;
    padding: 0;
    margin: 0 0 12px 0;
}
.reorder-fail-list li {
    padding: 6px 8px;
    border: 1px solid #eee;
    border-left: 3px solid #c0392b;
    border-radius: 4px;
    margin-bottom: 6px;
    font-size: 12px;
}
.reorder-fail-kind {
    display: inline-block;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.03em;
    color: #c0392b;
    margin-right: 6px;
}
.reorder-fail-name {
    font-weight: 600;
    color: #2a2a2a;
}
.reorder-fail-fix {
    display: block;
    color: #555;
    margin-top: 2px;
}
.reorder-fail-actions,
.reorder-fail-retry,
.reorder-fail-alt {
    margin-bottom: 10px;
}
/* Two stacked add choices: give the buttons a little breathing room. */
.reorder-fail-actions .reorder-mismatch-btn + .reorder-mismatch-btn {
    margin-top: 6px;
}
.reorder-fail-temp-note {
    font-size: 12px;
    color: #555;
    margin-top: 6px;
    margin-bottom: 0;
}
.reorder-fail-retry-note {
    font-size: 12px;
    font-weight: 600;
    color: #1e7e34;
    margin-bottom: 6px;
}
</style>